/**
 * CampanaNuevaRealPage — `/activa/real`
 *
 * El wizard de campaña corriendo contra el BACKEND REAL: sube los archivos, cruza, corre
 * el motor RFM, computa insights, genera mensajes con guardrail, envía y mide.
 *
 * Layout: stepper horizontal compacto (WizardStepper) + un panel de contenido a ancho
 * completo. La versión anterior usaba un nav vertical de 9 tarjetas dentro de un grid
 * `md:grid-cols-12`: por debajo de 768px el grid colapsaba a una columna y esas tarjetas
 * (~600px de alto) empujaban el formulario a y=967px — fuera de una pantalla de 814px.
 * Había que scrollear ~1000px para ver el campo que había que completar.
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, CheckCircle2, Loader2, UploadCloud, Table2, Sparkles,
  ListChecks, PenLine, Send, TrendingUp, MessageSquare,
  ShieldCheck, AlertTriangle, FileSpreadsheet, Users, Copy, ExternalLink, Smartphone,
  MessageCircle, Tag, Clock, Mail, RefreshCw, Download, BadgeCheck, Split,
} from "lucide-react";
import ReporteComercial from "../components/ReporteComercial";
import DeliveryFunnel from "../components/DeliveryFunnel";
import WizardStepper, { type Paso, type Fase } from "../components/WizardStepper";
import ContextoForm from "../components/ContextoForm";
import VozForm from "../components/VozForm";
import AsiSuena from "../components/AsiSuena";
import { useEnsayoVoz } from "../hooks/useEnsayoVoz";
import { VistaPreviaMensajes } from "../components/VistaPreviaMensajes";
import { ProgramarEnvio } from "../components/ProgramarEnvio";
import { ConfirmarEnvio } from "../components/ConfirmarEnvio";
import { ConectarCanal } from "../components/ConectarCanal";
import { FirmaComercio } from "../components/FirmaComercio";
import GestionCasillas from "../components/GestionCasillas";
import { RepartoWhatsApp } from "../components/RepartoWhatsApp";
import type { ContextReal, EstadoReparto } from "../types.real";
import { activaApi } from "../api";
import { compact, num, plural } from "../utils/format";
import { bloqueoEnvio, MOTIVO_EJEMPLO } from "../utils/bloqueoEnvio";
import { contextoPorDefecto } from "../utils/contextoPorDefecto";
import { esDeEjemplo, mensajeAlNoAbrir, NO_ENCONTRADA, pasoAlAbrir } from "../utils/retomarCampana";
import { resumenDelEnvio } from "../utils/resumenEnvio";
import { exigirOk, mensaje422Mapeo } from "../utils/exigirOk";
import { chipOferta, ofertaEnReporte, porQueBajoElDescuento, razonDelGrupo } from "../utils/grupos";
import { P, VERSION_PASOS, migrarPaso } from "../utils/pasosAsistente";
import { MOTIVO_OFICIAL_EJEMPLO, seguirPorOficial } from "../utils/porOficial";
import { motivoSinPorLosDos, resumenReparto } from "../utils/reparto";
import { marcaDeLaCampana, marcaQueFirma, nombreDeLaCampana } from "../utils/marcaCampana";
import { avisoSinPermiso } from "../utils/sinPermiso";
import { avisoSinColumnaConsentimiento, hayColumnaDeConsentimiento, queSeSalteaPorConsentimiento } from "../utils/consentimiento";
import { AlcanceOficial } from "../../wa-oficial/components/AlcanceOficial";
import { useAlcanceOficial } from "../../wa-oficial/hooks/useAlcanceOficial";
import { useCuentaOficialOpcional } from "../../wa-oficial/contexto";
import { RUTA_CARGA_BASE, porQueDifieren } from "../../wa-oficial/logic/alcance";
import { usePoliticaConsentimiento } from "../hooks/usePoliticaConsentimiento";
import { parseDataSinMuestras, puedeGuardarArchivos, PREFIJO_BORRADOR, PREFIJO_BORRADOR_ARCHIVOS } from "../utils/borradorSinDatos";
import pedidosCsv from "../sample/pedidos.csv?raw";
import clientesCsv from "../sample/clientes.csv?raw";
import { textoDeCsv } from "../../wa-oficial/logic/archivo";

// Clases LITERALES (Tailwind JIT no ve strings compuestos): borderL = borde izquierdo de la ficha,
// strong = el % de facturación, pillAlta = la pill de prioridad Alta (relleno lleno del color).
const ACCENTS: Record<string, any> = {
  g_dormidos: { bg: "bg-violet-50", text: "text-violet-700", ring: "ring-violet-200", dot: "bg-violet-500", borderL: "border-l-violet-500", strong: "text-violet-600", pillAlta: "bg-violet-500 text-white" },
  g_campeones: { bg: "bg-amber-50", text: "text-amber-700", ring: "ring-amber-200", dot: "bg-amber-500", borderL: "border-l-amber-500", strong: "text-amber-600", pillAlta: "bg-amber-500 text-white" },
  // H76: sin acento propio caía en el naranja de «En riesgo», justo el grupo del que se separó
  g_activos: { bg: "bg-cyan-50", text: "text-cyan-700", ring: "ring-cyan-200", dot: "bg-cyan-500", borderL: "border-l-cyan-500", strong: "text-cyan-600", pillAlta: "bg-cyan-500 text-white" },
  g_nuevos: { bg: "bg-emerald-50", text: "text-emerald-700", ring: "ring-emerald-200", dot: "bg-emerald-500", borderL: "border-l-emerald-500", strong: "text-emerald-600", pillAlta: "bg-emerald-500 text-white" },
  g_riesgo: { bg: "bg-orange-50", text: "text-orange-700", ring: "ring-orange-200", dot: "bg-orange-500", borderL: "border-l-orange-500", strong: "text-orange-600", pillAlta: "bg-orange-500 text-white" },
};

const withAccent = (grupos: any[]) => (grupos || []).map((g) => ({ ...g, accent: ACCENTS[g.id] || ACCENTS.g_riesgo }));

/** Los 9 pasos, cada uno con la frase que explica qué se hace acá. */
const STEPS: (Paso & { icon: any })[] = [
  { key: "carga", label: "Cargar datos", desc: "Subí los dos archivos que ya tenés: pedidos y clientes.", icon: UploadCloud },
  { key: "mapeo", label: "Vincular columnas", desc: "Confirmá qué es cada columna. Ya te propusimos cuál va con cuál.", icon: Table2 },
  { key: "reporte", label: "Reporte comercial", desc: "Lo que tus datos dicen de tu negocio, calculado de verdad.", icon: Sparkles },
  // El contexto son TRES decisiones distintas, no una pantalla larga: cómo hablás, qué das, cuándo sale.
  { key: "tono", label: "Tu voz", desc: "Con este tono se escriben todos los mensajes.", icon: MessageCircle },
  { key: "oferta", label: "Qué ofrecés", desc: "Lo que estás dispuesto a dar, y el techo que nunca se cruza.", icon: Tag },
  { key: "cuando", label: "Por dónde y cuándo", desc: "Los canales y el horario en el que se permite enviar.", icon: Clock },
  { key: "interno", label: "Plan de campañas", desc: "Un grupo, una campaña. Así queda repartida tu base.", icon: ListChecks },
  { key: "mensajes", label: "Mensajes", desc: "Un mensaje por grupo, con el techo ya aplicado.", icon: PenLine },
  // Un solo paso para conectar, con lo que la marca eligió en "Por dónde y cuándo": la línea de
  // WhatsApp, la casilla de email, o las dos. Eran dos pasos seguidos (uno por canal) y el asistente
  // llegaba a 13; ahora cada bloque aparece sólo si su canal se usa, así a quien manda sólo por
  // WhatsApp no le aparece un cartel sobre cuentas de email que no le toca resolver.
  { key: "canales", label: "Conectar", desc: "Desde dónde salen los mensajes: tu línea de WhatsApp, tu casilla de email, o las dos.", icon: Smartphone },
  { key: "envio", label: "Envío", desc: "Salen de a poco, salteando a quien pidió la baja y sin repetir a nadie.", icon: Send },
  // Las respuestas van adentro de Resultados: eran un paso aparte al que se llegaba con un botón más.
  { key: "resultados", label: "Resultados", desc: "Qué pasó con cada mensaje y lo que te contestaron.", icon: TrendingUp },
];

/** 11 pasos sueltos no dicen nada; agrupados en 3 fases, sí. */
const FASES: Fase[] = [
  { key: "datos", label: "Tus datos", pasos: [P.carga, P.mapeo] },
  { key: "estrategia", label: "La estrategia", pasos: [P.reporte, P.tono, P.oferta, P.cuando, P.plan, P.mensajes] },
  { key: "campana", label: "La campaña", pasos: [P.canales, P.envio, P.resultados] },
];

/** Encabezado consistente: en todos los pasos se sabe dónde estás y qué se espera de vos. */
function PasoHeader({ i }: { i: number }) {
  const s = STEPS[i];
  const Icon = s.icon;
  return (
    <div className="flex items-start gap-3.5 mb-5">
      <div className="w-10 h-10 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center flex-shrink-0">
        <Icon size={19} />
      </div>
      <div className="min-w-0">
        <h2 className="text-lg font-bold text-slate-900 tracking-tight leading-tight">{s.label}</h2>
        <p className="text-sm text-slate-500 mt-0.5 leading-relaxed">{s.desc}</p>
      </div>
    </div>
  );
}

/**
 * ── Borrador del wizard ──────────────────────────────────────────────────────────────────────
 *
 * Todo el avance del wizard vivía en memoria: un F5 —o cerrar la pestaña sin querer— borraba los
 * archivos cargados, el mapeo, la voz elegida y hasta a qué grupos ibas a mandar. Ahora se guarda
 * un borrador en localStorage y se restaura al volver.
 *
 * Dos claves: el estado LIVIANO se serializa en cada cambio (perillas, mapeo, selección); los
 * archivos CSV —lo pesado— van aparte y sólo se tocan cuando cambian, para no volver a serializar
 * megabytes de CSV en cada tecla. La clave lleva el brandId: dos marcas no comparten borrador.
 *
 * Las claves se arman AL MONTAR la página (`clavesBorrador`), no al importar el módulo: al importarlo, recién
 * logueado, el canje todavía no había llegado y la marca era la de demo. El equipo, que ahora entra a la inicial
 * (hatsu), guardaba el borrador con la clave de la demo y un F5 lo perdía. Montada, la marca ya es la del canje
 * (`PaginaSoloEquipo` espera el rol), y queda fija mientras la página vive: cambiar de marca recarga.
 */
function clavesBorrador(marca: string) {
  return { liviano: `${PREFIJO_BORRADOR}${marca}`, archivos: `${PREFIJO_BORRADOR_ARCHIVOS}${marca}` };
}
type ClavesBorrador = ReturnType<typeof clavesBorrador>;
/** un borrador de más de un día no se retoma: seguro es de otra sesión que quedó a medias */
const BORRADOR_TTL_MS = 24 * 3600 * 1000;

function leerBorrador(k: ClavesBorrador): any | null {
  try {
    const raw = localStorage.getItem(k.liviano);
    if (!raw) return null;
    const b = JSON.parse(raw);
    if (!b?.ts || Date.now() - b.ts > BORRADOR_TTL_MS) { limpiarBorrador(k); return null; }
    // Una campaña ya lanzada no se "retoma": retomarla sería editar algo que la gente ya recibió.
    // Se descarta y se arranca limpio (el efecto de guardado no alcanza: el estado ya se restauró).
    if (b?.campaign?.estado === "sent" || b?.campaign?.estado === "measured") { limpiarBorrador(k); return null; }
    // los archivos viven en su propia clave (pueden ser MB): se re-adjuntan acá
    try {
      const files = localStorage.getItem(k.archivos);
      if (files) b.files = JSON.parse(files);
    } catch { /* si los archivos no se pudieron leer, el resto del borrador igual sirve */ }
    return b;
  } catch { return null; }
}

function limpiarBorrador(k: ClavesBorrador) {
  try { localStorage.removeItem(k.liviano); localStorage.removeItem(k.archivos); } catch { /* nada que hacer */ }
}

/** El aviso de Resultados sobre los de este envío que nunca dieron permiso (el texto y el porqué, en `avisoSinPermiso`). */
function AvisoSinPermiso({ n }: { n: number }) {
  const a = avisoSinPermiso(n);
  if (!a) return null;
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3.5 flex items-start gap-2.5">
      <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
      <div>
        <p className="text-sm font-semibold text-amber-900">{a.titulo}</p>
        <p className="text-xs text-amber-700 mt-0.5">{a.detalle}</p>
      </div>
    </div>
  );
}

/** Botón principal del paso: una sola acción evidente, siempre al final del panel. */
function CTA({ onClick, disabled, loading, children, tono = "violeta" }: any) {
  const cls = tono === "verde" ? "bg-emerald-600 hover:bg-emerald-700" : tono === "oscuro" ? "bg-slate-900 hover:bg-slate-800" : "bg-violet-600 hover:bg-violet-700";
  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      className={`w-full h-12 rounded-xl ${cls} text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 transition-colors shadow-sm`}
    >
      {loading ? <Loader2 size={16} className="animate-spin" /> : children}
    </button>
  );
}


export default function CampanaNuevaRealPage() {
  const navigate = useNavigate();
  // El borrador se lee UNA vez al montar (lazy). Si hay algo guardado, cada estado arranca desde
  // ahí en vez de desde su default: por eso los useState de abajo usan `borrador0?.x ?? default`.
  const [params, setParams] = useSearchParams();
  /** «Seguir armando la campaña» desde el detalle: `?campana=<id>` abre ESA campaña, no el último borrador. */
  const campanaPedida = params.get("campana") || "";
  /** Las claves del borrador, de la marca sobre la que se montó la página (ver `clavesBorrador`). */
  const [claves] = useState(() => clavesBorrador(activaApi.brandId));
  const borrador0 = useMemo(() => {
    const b = leerBorrador(claves);
    // El borrador de la marca es de otra campaña: no se retoma, se abre la pedida (efecto de abajo).
    return campanaPedida && b?.campaign?.id !== campanaPedida ? null : b;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [restaurado, setRestaurado] = useState(!!borrador0);
  /**
   * Mientras llega la campaña pedida por id: sin esto se veía un instante el paso 1 con su botón. También con un
   * borrador de ESA campaña: el paso lo decide su estado en el backend, no el que quedó guardado (`pasoAlAbrir`).
   */
  const [cargandoCampana, setCargandoCampana] = useState(!!campanaPedida);

  const [current, setCurrent] = useState<number>(borrador0 ? migrarPaso(borrador0.current, borrador0.v) : 0);
  const [maxReached, setMaxReached] = useState<number>(borrador0 ? migrarPaso(borrador0.maxReached, borrador0.v) : 0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  /** código del error, para poder ofrecer la salida concreta (ej: NO_LINES → conectar WhatsApp) */
  const [errorCode, setErrorCode] = useState("");

  const [files, setFiles] = useState<{ pedidos?: string; clientes?: string }>(borrador0?.files ?? {});
  const [parseData, setParseData] = useState<any>(borrador0?.parseData ?? null);
  /** columna del cliente → campo DEENEX, por archivo. Es lo que el operador puede corregir. */
  const [mapeo, setMapeo] = useState<{ pedidos: Record<string, string>; clientes: Record<string, string> }>(borrador0?.mapeo ?? { pedidos: {}, clientes: {} });
  const [campaign, setCampaign] = useState<any>(borrador0?.campaign ?? null);
  const [grupos, setGrupos] = useState<any[]>(borrador0?.grupos ?? []);
  /** grupos elegidos para el envío, como lista EXPLÍCITA: [] = ninguno. La API usa la convención
   *  inversa (ausente = todos); la traducción se hace sólo al llamarla (ver `seleccionApi`). */
  const [gruposElegidos, setGruposElegidos] = useState<string[]>(borrador0?.gruposElegidos ?? []);
  /** líneas de WhatsApp elegidas para el envío. `todas` = mandar desde todas las conectadas (default).
   *  No va en el borrador: depende del estado de conexión EN VIVO, no de lo guardado. */
  const [lineasSel, setLineasSel] = useState<{ ids: string[]; todas: boolean }>({ ids: [], todas: true });
  // Contexto de marca COMPLETO. Antes esto eran dos useState (tono y techo) y los otros cinco
  // campos viajaban hardcodeados al backend: la marca nunca decidía qué se le ofrecía a su gente.
  const [contexto, setContexto] = useState<ContextReal>(borrador0?.contexto ?? contextoPorDefecto());
  /**
   * Por dónde sale la campaña. `false`: lo de siempre, el mensaje que arma Activa por las líneas de
   * WhatsApp (QR) y las casillas de email. `true`: por WhatsApp oficial (Twilio), con una plantilla
   * aprobada por Meta; los grupos de este análisis quedan como audiencia y la oferta elegida va en la
   * variable «Oferta». No va en `contexto.canales`: ese campo lo lee el envío de Activa.
   */
  const [porOficial, setPorOficial] = useState<boolean>(borrador0?.porOficial === true);
  /**
   * Por los dos: los WhatsApp repartidos entre las líneas por QR y WhatsApp oficial (pedido de Alan del 12/10, para
   * probar las dos vías con la misma base). El asistente sigue con la parte de QR; la de la API se sigue en WhatsApp
   * oficial. Excluye a `porOficial`: con las dos en false es «Líneas por QR o email», lo de siempre.
   */
  const [repartido, setRepartido] = useState<boolean>(borrador0?.repartido === true && borrador0?.porOficial !== true);
  /** lo que devolvió el backend del reparto (no va en el borrador: se vuelve a leer al abrir el paso) */
  const [estadoReparto, setEstadoReparto] = useState<EstadoReparto | null>(null);
  /** «lineas» | «oficial» | «ambos»: la elección de «¿Por dónde sale esta campaña?» en una sola palabra */
  const via: "lineas" | "oficial" | "ambos" = porOficial ? "oficial" : repartido ? "ambos" : "lineas";
  const elegirVia = (v: "lineas" | "oficial" | "ambos") => { setPorOficial(v === "oficial"); setRepartido(v === "ambos"); };
  /**
   * La voz que la marca usó la última vez. Sin esto, cada campaña nueva arrancaba con los
   * defaults y el dueño tenía que volver a elegir tono, trato, emoji y firma — y al que se
   * olvidaba le salía un mensaje con otra voz que la campaña anterior.
   */
  useEffect(() => {
    // Si estás retomando un borrador, ese contexto ya tiene la voz que elegiste en ESTA campaña:
    // pisarlo con la voz "recordada" de la marca sería deshacerte un cambio a mitad de camino.
    // Lo mismo con una campaña abierta por id: si ya tiene contexto, manda el suyo (efecto de abajo).
    if (borrador0 || campanaPedida) return;
    let vivo = true;
    activaApi
      .settings()
      .then((r: any) => {
        if (!vivo || r?.success === false) return;
        const { voz, tono } = r.data || {};
        if (!voz && !tono) return; // marca nueva: se quedan los defaults
        setContexto((c) => ({ ...c, ...(tono ? { tono } : {}), ...(voz ? { voz } : {}) }));
      })
      .catch(() => { /* si no se pudo leer, se sigue con los defaults */ });
    return () => { vivo = false; };
  }, []);

  /**
   * Abre la campaña pedida por id (`?campana=`): la trae del backend y arranca en el paso que le toca según
   * su estado (`pasoAlAbrir`), con su contexto si ya lo tiene. Si ya salió, al detalle. Después saca el
   * parámetro de la URL: desde ahí manda el borrador, como siempre (un F5 retoma donde quedó).
   *
   * Con un borrador de ESA campaña también se pide al backend: antes se abría en el paso guardado sin mirar el
   * estado, y uno que había quedado en «Cargar datos» abría el paso 1 de una campaña con los mensajes armados (o
   * abría una que ya había salido). Del borrador quedan las elecciones que todavía no se guardaron (la voz, la
   * oferta, los grupos tildados) y lo visitado.
   */
  useEffect(() => {
    if (!campanaPedida) return;
    const quitarDeLaUrl = () => setParams((p) => { const n = new URLSearchParams(p); n.delete("campana"); return n; }, { replace: true });
    // El borrador de la marca era de otra campaña y éste lo reemplaza: sus archivos también se van. Si no,
    // los de ejemplo de una prueba anterior se re-adjuntaban en el próximo F5 a esta campaña (`leerBorrador`).
    if (!borrador0) { try { localStorage.removeItem(claves.archivos); } catch { /* nada */ } }
    let vivo = true;
    // si se va al detalle, no se toca la URL: `setParams` navega relativo a ESTA página y lo traía de vuelta
    let alDetalle = false;
    activaApi.campaign(campanaPedida)
      .then(async (r: any) => {
        if (!vivo) return;
        const c = r?.data;
        if (!c) { setError(NO_ENCONTRADA); return; }
        const guardado = borrador0
          ? { current: migrarPaso(borrador0.current, borrador0.v), maxReached: migrarPaso(borrador0.maxReached, borrador0.v) }
          : null;
        const paso = pasoAlAbrir(c.estado, guardado);
        if (paso === null) {
          // ya salió: el borrador (que no lo sabía) no se retoma en el próximo /activa/real
          if (borrador0) limpiarBorrador(claves);
          alDetalle = true; navigate(`/activa/campanas/${encodeURIComponent(c.id)}`, { replace: true }); return;
        }
        setCampaign(c);
        // ya repartida entre QR y API: se abre en «Por los dos», no en «Líneas» con el aviso de que se va a sacar
        if (c.reparto && !borrador0) { setPorOficial(false); setRepartido(true); }
        if (borrador0) {
          // lo de este navegador manda en lo que todavía no se guardó; los grupos, si el backend los trae
          if (Array.isArray(c.grupos) && c.grupos.length) setGrupos(c.grupos);
        } else {
          setGrupos(Array.isArray(c.grupos) ? c.grupos : []);
          setGruposElegidos((c.grupos || []).map((g: any) => g.id));
          if (c.context) setContexto({ ...contextoPorDefecto(), ...c.context });
          else {
            // sin contexto todavía: la voz recordada de la marca, como en una campaña nueva
            const s: any = await activaApi.settings().catch(() => null);
            const { voz, tono } = (s?.success !== false && s?.data) || {};
            if (vivo && (voz || tono)) setContexto((x) => ({ ...x, ...(tono ? { tono } : {}), ...(voz ? { voz } : {}) }));
          }
        }
        setCurrent(paso.current); setMaxReached(paso.maxReached);
        setRestaurado(true);
      })
      // el 404 lanza (no es un 422): sin mirar el status, una campaña borrada decía «probá de nuevo en un rato»
      .catch((e) => { if (vivo) setError(mensajeAlNoAbrir(e)); })
      .finally(() => { if (vivo && !alDetalle) { setCargandoCampana(false); quitarDeLaUrl(); } });
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Al retomar, la campaña del borrador puede estar desactualizada (otra pestaña la envió, o el
   * backend cambió su estado). Se vuelve a pedir por id: el backend es la fuente de verdad. Si ya
   * no existe (la borraron), el borrador queda igual — mejor mostrar algo que perder todo.
   */
  useEffect(() => {
    const id = borrador0?.campaign?.id;
    // abierta por id, la trae (y decide el paso) el efecto de arriba
    if (!id || campanaPedida) return;
    let vivo = true;
    activaApi.campaign(id)
      .then((r: any) => {
        if (!vivo || !r?.data) return;
        setCampaign(r.data);
        if (Array.isArray(r.data.grupos) && r.data.grupos.length) setGrupos(r.data.grupos);
      })
      .catch(() => { /* la campaña ya no está: se sigue con lo del borrador */ });
    return () => { vivo = false; };
  }, []);

  /**
   * Guarda el borrador LIVIANO en cada cambio (todo menos los archivos, que van aparte).
   *
   * No persiste el estado vacío inicial —sin archivos ni campaña no hay nada que perder— ni una
   * campaña ya lanzada: cuando llega a "sent"/"measured" el borrador se borra, porque retomar una
   * campaña que ya salió sería empezar a "editar" algo que la gente ya recibió.
   */
  useEffect(() => {
    const hayProgreso = files.pedidos || files.clientes || campaign;
    if (!hayProgreso) return;
    if (campaign?.estado === "sent" || campaign?.estado === "measured") { limpiarBorrador(claves); return; }
    try {
      localStorage.setItem(claves.liviano, JSON.stringify({
        // analizada, sin las filas de muestra ni los ejemplos por columna (datos de clientes: ver borradorSinDatos)
        v: VERSION_PASOS, current, maxReached, parseData: campaign ? parseDataSinMuestras(parseData) : parseData,
        mapeo, campaign, grupos, gruposElegidos, contexto, porOficial, repartido, ts: Date.now(),
      }));
    } catch { /* cuota llena: el borrador liviano es chico, si no entra no hay mucho que hacer */ }
  }, [claves, current, maxReached, parseData, mapeo, campaign, grupos, gruposElegidos, contexto, porOficial, repartido, files.pedidos, files.clientes]);

  /**
   * Los archivos CSV, en su propia clave y su propio efecto: son lo pesado (pueden ser MB) y
   * cambian sólo al subir, no en cada tecla. Si no entran en la cuota se descartan en silencio:
   * antes de analizar el peor caso es tener que volver a elegir los archivos —que siguen en tu
   * compu—, no perder el resto.
   *
   * Analizada la campaña, los archivos propios se BORRAN del navegador: tienen los nombres, mails y
   * celulares de los clientes de la marca, ya viven en el backend, y antes quedaban guardados
   * indefinidamente si nadie volvía a abrir la página. Los de ejemplo se quedan (vienen con la app).
   */
  useEffect(() => {
    if (!files.pedidos && !files.clientes) {
      // Con una campaña y sin archivos en pantalla, los que hubiera guardados son de otro borrador.
      if (campaign) { try { localStorage.removeItem(claves.archivos); } catch { /* nada */ } }
      return;
    }
    const deEjemplo = !!files.clientes && files.clientes === clientesCsv;
    if (!puedeGuardarArchivos({ analizada: !!campaign, esEjemplo: deEjemplo })) {
      try { localStorage.removeItem(claves.archivos); } catch { /* nada */ }
      return;
    }
    try { localStorage.setItem(claves.archivos, JSON.stringify(files)); }
    catch { try { localStorage.removeItem(claves.archivos); } catch { /* nada */ } }
  }, [claves, files, campaign]);

  /* El "Así suena" lo calcula el wizard (no VozForm) y lo muestra pegado abajo, arriba del CTA,
     mientras se configura tono y oferta: así el impacto de cada cambio se ve sin scrollear hasta
     el preview. Un solo fetch, compartido entre el paso "Tu voz" (que muestra su desglose) y el
     de la oferta. El mensaje depende del contexto entero, así que refleja las dos cosas. */
  const { ensayo, cargando: ensayoCargando } = useEnsayoVoz(campaign?.marca, contexto);

  /* El techo que se le ANUNCIA al operador ("Techo del X% respetado") tiene que ser el que el
     backend aplicó de verdad, no el que quedó en el slider. Los puntos del stepper permiten
     saltar a un paso ya visitado sin pasar por el CTA, y el CTA es el único que persiste el
     contexto (doContext): mover el slider a 25% y saltar a "Mensajes" mostraba «Techo del 25%
     respetado» sobre mensajes generados con el techo viejo. Un cartel verde afirmando que se
     respetó un límite que nadie aplicó es exactamente la clase de mentira que este producto no
     puede permitirse — y encima sobre el guardrail que protege la plata del local.
     `campaign.context` es lo que quedó guardado; el estado local sólo vale antes de guardar. */
  const techo = campaign?.context?.techoPct ?? contexto.techoPct;
  /* Una franja con lista de días vacía significa TODOS los días, no ninguno: es el contrato del
     backend (wa-utils.ts:96 sólo filtra cuando `dias.length > 0`) y lo que dibuja el formulario
     (con la lista vacía prende los 7 chips).

     Esto valía `dias?.length === 0`, o sea justo al revés, y bloqueaba el botón "Ver el plan de
     campañas" apenas se elegía el primer preset —"Horario comercial · 9 a 21, todos los días"—
     que es el más obvio de los cuatro. El wizard quedaba trabado en el paso 6 sin explicar por
     qué, con los siete días marcados en pantalla.

     Hoy no hay forma de expresar "ningún día" (toggleDia impide apagar el último), así que no
     existe el estado que esta guarda pretendía atajar; se deja en false y documentado. */
  const sinDiasValidos = false;
  /** el operador destildó todos los grupos: no hay a quién escribirle (≠ "todavía no cargaron") */
  const sinGrupos = grupos.length > 0 && gruposElegidos.length === 0;
  /**
   * Los clientes cargados son los del dataset de ejemplo. Antes de analizar se deriva de los archivos
   * (el criterio son los CLIENTES porque los contactos son el riesgo: reemplazar sólo pedidos.csv deja a
   * las mismas personas del ejemplo); con la campaña analizada manda el backend (`esDeEjemplo`).
   */
  const esEjemplo = esDeEjemplo(campaign, files.clientes, clientesCsv);
  /** El comercio del título mientras todavía no hay campaña (después, el de la campaña). */
  const tituloMarca = marcaDeLaCampana({ esEjemplo, nombreLegible: activaApi.nombreActual(), brandId: activaApi.marcaActual(), esOperador: activaApi.esOperador() });
  const [sendResult, setSendResult] = useState<any>(null);
  /** el estado REAL de la campaña después de lanzar (lo trae el seguimiento): el cartel de Resultados sale de acá */
  const [estadoEnvio, setEstadoEnvio] = useState("");
  const [enviando, setEnviando] = useState(false);
  /** «Lanzar la campaña» abre la confirmación con el número real; recién ahí se manda */
  const [confirmarEnvio, setConfirmarEnvio] = useState(false);
  const [results, setResults] = useState<any>(null);
  const [responses, setResponses] = useState<any[]>([]);
  const [shareUrl, setShareUrl] = useState("");
  const [copied, setCopied] = useState(false);

  /**
   * Qué canales aplican, según lo elegido en "Por dónde y cuándo". El paso de un canal que la
   * marca no usa no se muestra ni se cuenta: a quien manda sólo por WhatsApp no se le puede
   * pedir que resuelva una casilla de email que no va a usar.
   */
  const usaWhatsApp = contexto.canales !== "Solo Email";
  const usaEmail = contexto.canales !== "Solo WhatsApp";
  const pasoOculto = (i: number) => i === P.canales && !usaWhatsApp && !usaEmail;
  /** los STEPS con la marca de oculto, que es lo que el stepper usa para numerar */
  const stepsVisibles = STEPS.map((s, i) => ({ ...s, oculto: pasoOculto(i) }));

  /**
   * Avanza salteando los pasos que no aplican. Sin esto, "siguiente" desde Mensajes caía en
   * "Conectar WhatsApp" aunque la campaña fuera sólo por email: una pantalla que pide vincular
   * un teléfono para mandar mails.
   */
  const saltar = (i: number, dir: 1 | -1): number => {
    let n = i;
    while (n > 0 && n < STEPS.length - 1 && pasoOculto(n)) n += dir;
    return Math.min(Math.max(n, 0), STEPS.length - 1);
  };
  const go = (i: number) => { const d = saltar(i, i >= current ? 1 : -1); setCurrent(d); setMaxReached((m) => Math.max(m, d)); setError(""); setErrorCode(""); };
  const run = async (fn: () => Promise<void>) => {
    // `errorCode` se limpia junto con `error`: sin esto quedaba pegado del último fallo y el
    // banner "Conectá WhatsApp" (que sólo aplica a NO_LINES) aparecía debajo de errores que no
    // tenían nada que ver — un parse fallido mostrando un botón para vincular un teléfono.
    setLoading(true); setError(""); setErrorCode("");
    try { await fn(); } catch (e: any) { setError(e?.message || "No pudimos conectar con el servidor. Probá de nuevo en un rato."); }
    finally { setLoading(false); }
  };

  /* Cambiar un archivo invalida TODO lo que se derivó del archivo anterior.
     `parseData`, `campaign`, `grupos`, `gruposElegidos`, `sendResult`, `results` y `responses`
     se calcularon con los dos CSV viejos; si sólo pisamos `files`, esos quedan colgados y los
     puntos del stepper dejan volver a "Reporte" y ver el diagnóstico del archivo ANTERIOR con
     el nuevo ya cargado — números reales de un negocio, sobre un archivo que no es ese. Todo lo
     derivado se limpia y el operador queda obligado a re-procesar, que es lo correcto: el
     reporte tiene que salir del archivo que está cargado, no de uno que ya no está. */
  const invalidarDerivados = () => {
    setParseData(null); setCampaign(null); setGrupos([]); setGruposElegidos([]);
    setSendResult(null); setResults(null); setResponses([]);
    setMaxReached(P.carga); // no se puede "saltar" a pasos que ya no tienen dato detrás
  };

  /**
   * Descarta el borrador retomado y arranca de cero. La salida para cuando lo que se guardó ya no
   * sirve (otra campaña, cambió todo): sin esto, retomar sería una trampa —siempre volvés a lo
   * mismo y no hay forma de empezar limpio.
   */
  const empezarDeNuevo = () => {
    limpiarBorrador(claves);
    setRestaurado(false);
    invalidarDerivados();
    setFiles({}); setMapeo({ pedidos: {}, clientes: {} });
    setContexto(contextoPorDefecto());
    // la elección de canal es de la campaña anterior: sin esto, la nueva arrancaba con «WhatsApp oficial» marcado
    setPorOficial(false);
    setRepartido(false);
    setEstadoReparto(null);
    setCurrent(P.carga); setError(""); setErrorCode("");
  };
  const cargarEjemplo = () => { invalidarDerivados(); setFiles({ pedidos: pedidosCsv, clientes: clientesCsv }); };
  const onFile = (which: "pedidos" | "clientes") => (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; if (!f) return;
    void textoDeCsv(f).then((texto) => { invalidarDerivados(); setFiles((prev) => ({ ...prev, [which]: texto })); });
  };

  const doParse = () => run(async () => {
    const r = await activaApi.parse({ content: files.pedidos!, encoding: "csv" }, { content: files.clientes!, encoding: "csv" });
    // arranca con lo que sugirió la máquina (o el mapeo ya confirmado antes para esta marca),
    // pero es EDITABLE: la sugerencia es un borrador, no una decisión.
    const inicial = (arch: "pedidos" | "clientes") => {
      const o: Record<string, string> = {};
      for (const s of r.data[arch].sugerencia) o[s.columnaCliente] = s.campoDeenex;
      const guardado = r.data[arch].mapeoGuardado;
      if (guardado) for (const [campo, col] of Object.entries(guardado as Record<string, string>)) o[col] = campo;
      return o;
    };
    setMapeo({ pedidos: inicial("pedidos"), clientes: inicial("clientes") });
    setParseData(r.data); go(P.mapeo);
  });

  /** columna→campo (lo que edita el operador) ⇒ campo→columna (lo que espera el backend). */
  /**
   * campo → [columnas]. Si un campo quedó asignado a MÁS DE UNA columna, es un conflicto que hay
   * que mostrar, no resolver por nuestra cuenta.
   */
  const conflictosDe = (arch: "pedidos" | "clientes") => {
    const porCampo = new Map<string, string[]>();
    for (const [columna, campo] of Object.entries(mapeo[arch] || {})) {
      if (!campo || campo === "(ignorar)") continue;
      porCampo.set(campo, [...(porCampo.get(campo) || []), columna]);
    }
    return new Map([...porCampo].filter(([, cols]) => cols.length > 1));
  };
  const hayConflictos = conflictosDe("pedidos").size > 0 || conflictosDe("clientes").size > 0;

  const lookupDe = (arch: "pedidos" | "clientes") => {
    /**
     * Colapsaba en silencio: con dos columnas mapeadas al MISMO campo ganaba la última y la otra
     * desaparecía, mientras la UI seguía mostrando las dos con el ✓ verde. Si el operador eligió
     * mal, la campaña corría sobre la columna equivocada sin un solo aviso. Ahora el conflicto se
     * bloquea antes (ver `hayConflictos`), así que acá ya no puede haber duplicados.
     */
    const o: Record<string, string> = {};
    for (const [columna, campo] of Object.entries(mapeo[arch] || {})) if (campo && campo !== "(ignorar)") o[campo] = columna;
    return o;
  };

  const doAnalyze = () => run(async () => {
    // La marca que firma los mensajes: la del comercio elegido, nunca «HATSU» fijo (ver marcaDeLaCampana).
    // Lo que se analiza son los archivos cargados: si son los de ejemplo lo dicen ellos, no una campaña anterior.
    const deEjemplo = esDeEjemplo(null, files.clientes, clientesCsv);
    const marca = marcaDeLaCampana({ esEjemplo: deEjemplo, nombreLegible: activaApi.nombreActual(), brandId: activaApi.marcaActual(), esOperador: activaApi.esOperador() });
    const r = await activaApi.analyze(
      nombreDeLaCampana(), marca,
      { content: files.pedidos!, encoding: "csv" }, { content: files.clientes!, encoding: "csv" },
      lookupDe("pedidos"), lookupDe("clientes"),
      deEjemplo ? "ejemplo" : undefined,
    );
    if (r.status === 422) {
      // 422 no es un error: es el sistema negándose a adivinar. Cada código pide algo distinto
      // (el texto lo arma mensaje422Mapeo, el mismo que usa la carga del seguimiento).
      setError(mensaje422Mapeo(r));
      return;
    }
    setCampaign(r.data.campaign); setGrupos(r.data.campaign.grupos);
    /* Reprocesar crea OTRA campaña, sin contexto ni mensajes: lo visitado de la anterior ya no vale. Sin
       esto, los puntos del stepper dejaban saltar de «Vincular columnas» a «Plan» y «Armar los mensajes»
       corría sobre la campaña nueva sin la oferta elegida (el backend ahora lo frena con SIN_CONTEXTO).
       Se baja ANTES de `go`, que sólo sube el máximo. */
    setMaxReached(P.reporte);
    go(P.reporte);
  });
  /** La campaña que devolvió el backend al cambiar el nombre que firma (si ya había mensajes, vienen rearmados). */
  const adoptarCampana = (c: any) => {
    setCampaign(c);
    if (Array.isArray(c?.grupos) && c.grupos.length) setGrupos(c.grupos);
  };
  /** sin el nombre del comercio no se arman los mensajes ni se envía (el backend tampoco: SIN_MARCA) */
  const sinMarca = !!campaign && !marcaQueFirma(campaign.marca);
  /**
   * Con «Líneas» o «WhatsApp oficial» elegido, un reparto que quedó guardado de antes (se eligió «Por los dos» y después
   * se cambió de idea) se saca: si no, cada vía seguiría escribiéndole sólo a su parte. false = no se pudo (ya salió
   * una parte, por ejemplo): el motivo queda en el error y no se sigue.
   */
  const sacarRepartoSiNoVa = async (): Promise<boolean> => {
    if (via === "ambos" || !campaign?.reparto) return true;
    const r = await activaApi.quitarReparto(campaign.id);
    if (r?.success === false) { setError(r.error || "No pudimos sacar el reparto entre QR y API."); return false; }
    setCampaign({ ...campaign, reparto: null });
    return true;
  };
  /** Se guarda al terminar las TRES preguntas del contexto (tono → oferta → cuándo), no antes. */
  const doContext = () => run(async () => {
    if (!(await sacarRepartoSiNoVa())) return;
    // se manda lo que la marca eligió, no constantes escondidas en el código
    const r = await activaApi.context(campaign.id, contexto);
    // el endpoint devuelve la campaña con el contexto YA guardado: se adopta para que los avisos
    // del paso de mensajes (el techo, sobre todo) hablen de lo que el backend aplicó y no de lo
    // que quedó en el slider. Sin esto, `campaign.context` seguía siendo el de `analyze` (vacío).
    if (r?.data) setCampaign(r.data);
    go(P.plan);
  });
  /**
   * Por WhatsApp oficial: se guarda el contexto (la oferta que eligió la marca es la que va en la variable
   * «Oferta»: sin guardarla saldría el % del motor, que la marca no autorizó), se crea el borrador de la
   * campaña de WhatsApp oficial con los grupos de este análisis como audiencia y se sigue allá, eligiendo
   * la plantilla. El borrador de este asistente se descarta: la campaña sigue en WhatsApp oficial y el
   * análisis queda en Campañas.
   */
  const doOficial = () => run(async () => {
    if (!(await sacarRepartoSiNoVa())) return;
    const r = await activaApi.context(campaign.id, contexto);
    if (r?.success === false) { setError(r.error || "No pudimos guardar la oferta."); return; }
    if (r?.data) setCampaign(r.data);
    // si ya salió una campaña oficial de este análisis, se sigue ésa en vez de crear otra (`seguirPorOficial`)
    const c = await seguirPorOficial(campaign);
    if (!c.ok) { setError(c.error); return; }
    limpiarBorrador(claves);
    navigate(c.ruta);
  });
  /**
   * La parte de la API de una campaña repartida: la campaña de WhatsApp oficial de este análisis (la que ya hay o una
   * nueva, `seguirPorOficial`), que lee sólo a los asignados a la API. El borrador de este asistente NO se descarta:
   * la parte de QR sigue acá.
   */
  const doParteApi = () => run(async () => {
    // desde «Por dónde y cuándo» la oferta todavía no se guardó, y la plantilla del oficial la usa: se guarda antes
    if (current === P.cuando) {
      const r = await activaApi.context(campaign.id, contexto);
      if (r?.success === false) { setError(r.error || "No pudimos guardar la oferta."); return; }
      if (r?.data) setCampaign(r.data);
    }
    const c = await seguirPorOficial(campaign);
    if (!c.ok) { setError(c.error); return; }
    navigate(c.ruta);
  });
  const doMessages = () => run(async () => {
    // `req()` devuelve el 422 sin lanzar: sin `exigirOk`, un SIN_CONTEXTO o un SIN_MARCA rompía en `r.data.grupos` y la
    // pantalla mostraba «Cannot read properties of undefined (reading 'grupos')» en vez del motivo.
    const r = exigirOk(await activaApi.messages(campaign.id));
    setGrupos(r.data.grupos);
    // Arranca con TODOS elegidos, explícitamente. La selección se guarda como la lista real de
    // ids: así `[]` quiere decir "ninguno" y nada más. Con la convención vieja ("vacío = todos",
    // la de la API) destildar el último grupo dejaba la lista vacía y volvía a significar todos.
    setGruposElegidos((r.data.grupos || []).map((g: any) => g.id));
    go(P.mensajes);
  });
  /**
   * La selección del operador traducida a la convención de la API, que es la inversa: allá
   * `undefined` = todos. Sólo se manda la lista cuando es un subconjunto real; nunca `[]` para
   * grupos (sería "todos" del otro lado; con cero elegidos el CTA ya está bloqueado). Líneas:
   * misma convención (undefined = todas las conectadas), elegidas en el paso de conexión.
   *
   * Un solo lugar para el modal de confirmación y para doSend: si cada uno tradujera por su lado,
   * el número que el operador aprueba y el envío real podrían divergir.
   */
  const seleccionApi = () => ({
    grupos: gruposElegidos.length === grupos.length ? undefined : gruposElegidos,
    lineas: lineasSel.todas ? undefined : lineasSel.ids,
  });
  const doSend = () => run(async () => {
    setErrorCode("");
    const sel = seleccionApi();
    const r = await activaApi.send(campaign.id, sel.grupos, sel.lineas);
    if (r.status === 422) {
      // Guardamos el CÓDIGO, no sólo el texto: un error que dice "conectá una línea" pero no te
      // deja ir a conectarla es un callejón sin salida — hay que salir del wizard y buscarla.
      const esSinLineas = r.code === "NO_LINES" || r.error === "NO_LINES" || r.error?.includes("línea");
      setErrorCode(esSinLineas ? "NO_LINES" : "");
      setError(esSinLineas ? "Todavía no hay ninguna línea de WhatsApp conectada, así que los mensajes no pueden salir." : (r.error || "No se pudo enviar."));
      return;
    }
    setSendResult(r.data);
    setEstadoEnvio(r.data?.estado || "enviando");
    setEnviando(true); go(P.resultados);
    try {
      // el envío corre en background (worker con delays humanizados): pollear hasta "sent".
      // un error transitorio de red en una vuelta NO debe abortar el loop ni colgar el spinner.
      // La campaña que vuelve se adopta: el cartel de Resultados dice lo que pasó de verdad (antes
      // quedaba «en la fila de envío» con todo ya enviado).
      for (let i = 0; i < 60; i++) {
        await new Promise((res) => setTimeout(res, 700));
        try {
          const c = await activaApi.campaign(campaign.id);
          if (c.data) { setCampaign(c.data); setEstadoEnvio(c.data.estado); }
          if (c.data && c.data.estado !== "enviando") break;
        } catch { /* transitorio: seguir polleando */ }
      }
      const rr = await activaApi.results(campaign.id).catch(() => null);
      if (rr?.data) setResults(rr.data);
    } finally {
      setEnviando(false); // pase lo que pase, salimos del spinner
    }
  });
  const doShare = () => run(async () => {
    const r = await activaApi.share(campaign.id);
    const url = `${window.location.origin}/r/${r.data.token}`;
    setShareUrl(url);
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2500); } catch { /* clipboard opcional */ }
  });
  // Revocar el link (H14): confirmado ANTES de `run`, así cancelar no limpia el error a la vista.
  const doUnshare = () => {
    if (!window.confirm("¿Dejar de compartir el reporte? El link actual deja de funcionar para todos los que lo tengan. Si después lo volvés a compartir, se crea un link nuevo.")) return;
    return run(async () => {
      await activaApi.unshare(campaign.id);
      setShareUrl("");
      setCopied(false);
    });
  };
  /* Bajar la segmentación ACÁ y no sólo en el detalle: este es el paso donde se ven los grupos
     por primera vez y todavía no se invirtió en tono, oferta ni mensajes. Si el criterio no
     cierra, conviene enterarse antes de generar mensajes para cuatro grupos mal armados. */
  const [bajandoSeg, setBajandoSeg] = useState(false);
  const bajarSegmentacion = async () => {
    if (!campaign?.id) return;
    setBajandoSeg(true);
    // mismo criterio que `run`: limpiar error + errorCode antes de reintentar. No se reusa
    // `run` entero porque comparte `loading` con el CTA principal del wizard, y acá sólo tiene
    // que quedar ocupado (y avisar) el botón de la segmentación, no el paso completo.
    setError(""); setErrorCode("");
    try { await activaApi.descargarSegmentacionXLSX(campaign.id); }
    catch (e) { setError((e as Error)?.message || "No se pudo bajar la segmentación"); }
    finally { setBajandoSeg(false); }
  };
  /* Antes era `window.print()`: abría el diálogo de impresión del navegador y no bajaba nada.
     Ahora el backend renderiza el reporte con Chromium y devuelve un PDF de verdad. Genera
     Chromium headless del lado del servidor, así que puede tardar unos segundos — de ahí el
     estado "Generando…", mismo criterio que `bajandoSeg`. */
  const [descargandoPDF, setDescargandoPDF] = useState(false);
  const bajarReportePDF = async () => {
    if (!campaign?.id) return;
    setDescargandoPDF(true);
    setError(""); setErrorCode("");
    try { await activaApi.descargarReportePDF(campaign.id); }
    catch (e) { setError((e as Error)?.message || "No se pudo generar el PDF"); }
    finally { setDescargandoPDF(false); }
  };
  const doMeasure = () => run(async () => {
    await activaApi.measure(campaign.id);
    const rr = await activaApi.results(campaign.id); setResults(rr.data);
  });
  // Base de SEGUIMIENTO: los 2 CSV actualizados (acumulativos) del período posterior. Cierra la
  // medición del piloto sin esperar el cambio de mes calendario.
  const [segFiles, setSegFiles] = useState<{ pedidos?: string; clientes?: string }>({});
  const onSegFile = (which: "pedidos" | "clientes") => (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; if (!f) return;
    void textoDeCsv(f).then((texto) => setSegFiles((prev) => ({ ...prev, [which]: texto })));
  };
  const doSeguimiento = () => run(async () => {
    if (!segFiles.pedidos || !segFiles.clientes) return;
    const r = await activaApi.cargarSeguimiento(campaign.id, { content: segFiles.pedidos, encoding: "csv" }, { content: segFiles.clientes, encoding: "csv" });
    // un 422 (faltan columnas, mapeo ambiguo) vuelve sin lanzar: se muestra y los archivos quedan
    if (r?.success === false) { setError(mensaje422Mapeo(r)); return; }
    const rr = await activaApi.results(campaign.id); setResults(rr.data);
    setSegFiles({});
  });
  /** Las respuestas viven en Resultados: se traen solas cuando el envío terminó, y con «Actualizar». */
  const [cargandoRespuestas, setCargandoRespuestas] = useState(false);
  const cargarRespuestas = async () => {
    if (!campaign?.id) return;
    setCargandoRespuestas(true);
    try { const r = await activaApi.responses(campaign.id); setResponses(r.data || []); }
    catch { /* la lista de respuestas es opcional: los resultados se siguen viendo */ }
    finally { setCargandoRespuestas(false); }
  };
  const hayResultados = !!results;
  /**
   * Un envío largo (30–90 s por mensaje) sigue después de que el asistente dejó de esperarlo: mientras la
   * campaña esté «enviando» y estés en Resultados, se refrescan la campaña y la tabla cada 10 s. Sin esto la
   * tabla quedaba congelada en lo que había salido a los ~40 s y el cartel seguía diciendo «en la fila».
   */
  useEffect(() => {
    const id = campaign?.id;
    if (current !== P.resultados || enviando || estadoEnvio !== "enviando" || !id) return;
    let vivo = true;
    const t = setInterval(async () => {
      const c = await activaApi.campaign(id).catch(() => null);
      if (!vivo || !c?.data) return;
      setCampaign(c.data); setEstadoEnvio(c.data.estado);
      const rr = await activaApi.results(id).catch(() => null);
      if (vivo && rr?.data) setResults(rr.data);
    }, 10_000);
    return () => { vivo = false; clearInterval(t); };
  }, [current, enviando, estadoEnvio, campaign?.id]);
  useEffect(() => {
    if (current === P.resultados && !enviando && hayResultados && campaign?.id) void cargarRespuestas();
    // sólo al llegar a Resultados con el envío terminado (no en cada render)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, enviando, hayResultados, campaign?.id]);

  const report = campaign?.report;
  const reporteFront = report && {
    marca: campaign.marca, plataDormida: report.plataDormida, concentracion: report.concentracion,
    baseDormida: report.baseDormida, totalClientes: report.totalClientes, rotativo: report.rotativo,
  };
  /**
   * Hasta cuántos de este análisis pueden recibir por WhatsApp oficial (la cuenta del chequeo, sin armar la campaña).
   * Se pide en el reporte y en «Por dónde y cuándo»: un archivo sin quién aceptó llegaba hasta confirmar la campaña
   * oficial y ahí recién daba «no hay a quién mandarle». Es la misma cuenta que muestra después la Audiencia.
   */
  const alcanceOficial = useAlcanceOficial(current === P.reporte || current === P.cuando ? campaign?.id : null);
  // La regla de fatiga de la marca (29/10/2026): vive en la config del oficial y vale también para las líneas. El
  // proveedor de la cuenta va en el layout de Activa; sin él (o sin cargar), «Cuándo» la explica sin los números.
  const configOficial = useCuentaOficialOpcional()?.config ?? null;
  const reglaMarca = configOficial
    ? { dias: Number(configOficial.fatigaDias) || 0, maximo: configOficial.fatigaMaximo ?? 1, contarMails: configOficial.fatigaContarMails === true }
    : null;
  /**
   * Por qué el mismo archivo le llega a unos por un canal y no por el otro: depende de la política de consentimiento
   * de la marca. Se pide en los pasos que hablan del consentimiento (también el reporte y el envío, que dicen a quién
   * se le escribe); mientras no llega, no se dice nada.
   */
  const politicaOptIn = usePoliticaConsentimiento(current === P.mapeo || current === P.reporte || current === P.cuando || current === P.envio);
  const porQueCanales = politicaOptIn.cargando ? null : porQueDifieren(politicaOptIn.politica);
  /** el análisis es del dataset de ejemplo: por el oficial no sale, y eso ya lo dice su propio aviso */
  const analisisDeEjemplo = !!(esEjemplo || campaign?.esEjemplo);
  /** con 0, «Seguir en WhatsApp oficial» llevaba a una campaña que no podía salir */
  const oficialSinNadie = !analisisDeEjemplo && alcanceOficial.alcance?.pueden === 0;
  /** sin la cuenta de WhatsApp oficial, «Por los dos» dejaría a los de la API sin recibir por ninguna vía */
  const sinPorLosDos = motivoSinPorLosDos(alcanceOficial.alcance);
  const listo = !!files.pedidos && !!files.clientes;
  // "volvieron" sólo vale si se comparó contra una base posterior; si no es "sin medir", no 0.
  const atribucionPendiente = results?.atribucion !== "exacta-por-snapshot";

  /**
   * La acción principal de cada paso, centralizada acá.
   * Antes cada paso terminaba con su propio <CTA> al final del contenido: en los pasos largos
   * (el reporte, los mensajes) el botón quedaba a ~1500px de scroll y había que ir a buscarlo.
   * Sacándola del contenido, la barra de abajo puede quedar fija y siempre a la vista.
   */
  const accionPaso = (): { label: React.ReactNode; onClick: () => void; tono?: string; disabled?: boolean } | null => {
    switch (current) {
      // el label dice POR QUÉ está deshabilitado: un botón gris mudo no ayuda a nadie
      case P.carga: return { label: listo ? <>Vincular columnas <ArrowRight size={16} /></> : "Subí los dos archivos para seguir", onClick: doParse, disabled: !listo };
      // con un campo asignado a dos columnas no se avanza: una de las dos se perdería en silencio
      // Retomado después de analizar, los archivos propios ya no están en el navegador (se borran al
      // analizar): para procesar de nuevo hay que volver a subirlos, no mandar un análisis vacío.
      case P.mapeo: return !listo ? { label: <>Volvé a subir los archivos para procesar de nuevo <ArrowRight size={16} /></>, onClick: () => go(P.carga) } : {
        label: hayConflictos ? "Resolvé las columnas repetidas para seguir" : <>Procesar y ver el reporte <ArrowRight size={16} /></>,
        onClick: doAnalyze,
        disabled: hayConflictos,
      };
      case P.reporte: return { label: <>Configurar las campañas <ArrowRight size={16} /></>, onClick: () => go(P.tono), tono: "oscuro" };
      // el contexto avanza de a una decisión; recién al final se guarda (doContext)
      case P.tono: return { label: <>Qué le podés ofrecer <ArrowRight size={16} /></>, onClick: () => go(P.oferta) };
      case P.oferta: return { label: <>Por dónde y cuándo <ArrowRight size={16} /></>, onClick: () => go(P.cuando) };
      case P.cuando: return porOficial
        // los datos de ejemplo son de personas ajenas a la marca: por el oficial saldrían de verdad
        ? (analisisDeEjemplo
          ? { label: MOTIVO_OFICIAL_EJEMPLO, onClick: () => {}, disabled: true }
          // nadie dijo que sí (o nadie tiene un celular que sirva): el porqué y el otro canal están arriba
          : oficialSinNadie
            ? { label: "Nadie de este análisis puede recibir por WhatsApp oficial", onClick: () => {}, disabled: true }
            : { label: <>Seguir en WhatsApp oficial <ArrowRight size={16} /></>, onClick: doOficial })
        // por los dos: el reparto se guarda en su panel; sin guardarlo no hay a quién mandarle por cada vía
        : repartido && !estadoReparto?.reparto
          ? { label: "Guardá el reparto entre QR y API para seguir", onClick: () => {}, disabled: true }
          // un reparto guardado de antes de desconectar la cuenta: los de la API no recibirían
          : repartido && sinPorLosDos && (estadoReparto?.reparto?.api ?? 0) > 0
            ? { label: "Conectá WhatsApp oficial o elegí otra vía", onClick: () => {}, disabled: true }
            : { label: repartido ? <>Seguir con la parte por QR <ArrowRight size={16} /></> : <>Ver el plan de campañas <ArrowRight size={16} /></>, onClick: doContext, disabled: sinDiasValidos };
      // sin el nombre del comercio los mensajes dirían «te extrañamos en Tu marca»: se pide arriba, en este paso
      case P.plan: return sinMarca
        ? { label: "Poné el nombre de tu comercio para armar los mensajes", onClick: () => {}, disabled: true }
        : { label: <>Armar los mensajes <PenLine size={15} /></>, onClick: doMessages };
      // sin ningún grupo elegido no hay nada que enviar: el botón lo dice en vez de mandar a todos
      case P.mensajes: return sinGrupos
        ? { label: "Elegí al menos un grupo para seguir", onClick: () => {}, disabled: true }
        // go() saltea los pasos de canal que no aplican, así que apuntar al primero siempre
        // aterriza donde corresponde (y si no hay ninguno, directo al envío).
        : { label: <>Por dónde va a salir <ArrowRight size={16} /></>, onClick: () => go(P.canales) };
      // Se puede seguir sin conectar nada: el envío lo vuelve a chequear y lo dice con su motivo.
      // Trabar el wizard acá dejaría al operador sin poder ni siquiera programar la campaña.
      case P.canales: return { label: <>Ir al envío <ArrowRight size={16} /></>, onClick: () => go(P.envio) };
      // ya agendada: mandar ahora tiene que ser una decisión explícita (cancelar la programación),
      // no un botón verde que sigue ahí como si la agenda no existiera. Lo mandado no se deshace.
      // El motivo va en el propio botón (programada, sin grupos, dataset de ejemplo): un botón gris
      // mudo no le dice al operador qué hacer.
      case P.envio: {
        const motivo = bloqueoEnvio(campaign, { sinGrupos, archivosDeEjemplo: esEjemplo, sinMarca });
        // Con los datos de ejemplo no hay nada que destrabar acá: lo que sigue es subir los del comercio.
        if (motivo === MOTIVO_EJEMPLO) return { label: <>Son datos de ejemplo · Subir los del comercio <ArrowRight size={16} /></>, onClick: empezarDeNuevo };
        return motivo
          ? { label: motivo, onClick: () => {}, disabled: true }
          // no manda: abre la confirmación con el número real (dry-run) de esta misma selección
          : { label: <><Send size={15} /> Lanzar la campaña</>, onClick: () => setConfirmarEnvio(true), tono: "verde" };
      }
      // Resultados es el final: mientras el worker despacha no hay nada que apretar, y después las
      // respuestas ya están en la misma pantalla.
      default: return null;
    }
  };
  const accion = accionPaso();
  // en el primer paso "atrás" es salir del wizard: dejarlo muerto sería un botón que miente
  const atras = () => (current === P.carga ? navigate("/activa") : go(saltar(current - 1, -1)));
  // El "Así suena" vive SÓLO en el paso de la voz: es donde se decide cómo se escribe el mensaje
  // y donde ver el efecto de cada perilla importa. En los demás pasos (oferta, plan, franja) era
  // ruido pegado al pie que competía con lo que había que mirar ahí.
  const verAsiSuena = current === P.tono;

  if (cargandoCampana) {
    return (
      <div className="min-h-full flex items-center justify-center p-8 text-sm text-slate-500 gap-2" aria-busy="true">
        <Loader2 size={16} className="animate-spin text-violet-500" /> Abriendo la campaña…
      </div>
    );
  }

  return (
    // sin padding abajo: el último elemento es la barra anclada, que trae su propio espaciado.
    // Con `pb` acá la barra quedaba flotando sobre ese padding (un margin negativo NO la baja:
    // sólo achica el espacio que ocupa, no la mueve).
    // min-h-full (no min-h-screen): el alto de referencia es el contenedor scrolleable, que mide
    // MENOS que la ventana (arriba hay barras del layout). Con 100vh el wrapper se pasaba unos px
    // y la barra terminaba asomando por debajo del piso.
    <div className="min-h-full flex flex-col px-4 pt-4 sm:px-6 sm:pt-6 md:px-8 md:pt-8 animate-fade-in">
      {/* w-full NO es redundante: este div es flex-item del `flex flex-col` de arriba, y en flex
          los márgenes `auto` del eje cruzado (mx-auto) absorben el espacio libre Y anulan el
          stretch. Sin w-full el paso se encogía al ancho de su contenido (571px de los 896 que
          declara max-w-4xl) y quedaba angosto, flotando entre dos márgenes enormes y —peor—
          desalineado contra la barra anclada de abajo, que sí mide 896 porque su padre es block. */}
      <div className="w-full mx-auto max-w-4xl space-y-5">
        <Link to="/activa" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-violet-600 transition-colors">
          <ArrowLeft size={14} /> Campañas
        </Link>

        {/*
          Identidad en UNA línea. Antes eran tres renglones (chip, título grande y claim) que se
          repetían en los 11 pasos sin decir nunca en qué paso estabas: ~120px de alto donde lo
          único vivo era el stepper de abajo.
          El claim va sólo en la carga, que es donde significa algo ("lo que subas se calcula");
          repetirlo sobre el reporte o sobre los mensajes es ruido.
        */}
        <div className="flex items-center gap-2.5 flex-wrap">
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 flex-shrink-0">
            WhatsApp · QR
          </span>
          {/* con una campaña ya analizada, su nombre: retomada desde el detalle, «Campaña nueva» no decía cuál era */}
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">{campaign?.nombre || "Campaña nueva"}{(campaign ? marcaQueFirma(campaign.marca) : tituloMarca) ? ` · ${campaign ? marcaQueFirma(campaign.marca) : tituloMarca}` : ""}</h1>
        </div>
        {current === P.carga && (
          <p className="text-sm text-slate-500 -mt-2">Todo lo que ves acá se calcula desde tus archivos.</p>
        )}

        {/* Retomaste un borrador: se dice explícito, con la salida para descartarlo. Un F5 que
            restaura sin avisar confunde tanto como uno que borra todo. Se muestra hasta que tocás
            algo (empezarDeNuevo lo baja); sólo mientras hay progreso real que valía la pena guardar. */}
        {restaurado && (files.pedidos || files.clientes || campaign) && (
          <div className="rounded-xl border border-violet-200 bg-violet-50/60 p-3 flex items-center gap-2.5 flex-wrap">
            <RefreshCw size={15} className="text-violet-500 flex-shrink-0" />
            <p className="text-sm text-violet-900 flex-1 min-w-0">Retomamos tu campaña donde la dejaste.</p>
            <button onClick={empezarDeNuevo} className="text-xs font-semibold text-violet-600 hover:text-violet-800 underline underline-offset-2 flex-shrink-0">
              Empezar de nuevo
            </button>
          </div>
        )}

        {/* Stepper: ~90px, contra los ~600px del nav vertical anterior */}
        <WizardStepper steps={stepsVisibles} fases={FASES} current={current} maxReached={maxReached} onGo={go} />

        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-3.5 text-sm text-red-700 flex items-start gap-2.5">
            <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p>{error}</p>
              {/* La salida va ACÁ, al lado del problema — y ADENTRO del formulario.
                  Antes era un link a /activa/whatsapp: el operador se iba con los archivos
                  cargados, el mapeo confirmado y los mensajes generados, y al volver estaba de
                  nuevo en el paso 1. Conectar es un requisito del envío, no un trámite de otra
                  sección. */}
              {errorCode === "NO_LINES" && (
                <div className="mt-3">
                  <ConectarCanal onCambio={() => { setError(""); setErrorCode(""); }} compacto />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Panel del paso — ancho completo y visible sin scrollear */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 sm:p-6">
          {/* 0 · Carga */}
          {current === P.carga && (
            <div>
              <PasoHeader i={P.carga} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {([
                  { k: "pedidos", t: "Reporte de pedidos", h: "Qué compró cada cliente y cuándo" },
                  { k: "clientes", t: "Reporte de clientes", h: "Nombre, mail y teléfono de cada uno" },
                ] as const).map((f) => (
                  <label
                    key={f.k}
                    className={`group rounded-2xl border-2 border-dashed p-5 text-center cursor-pointer transition-all ${
                      files[f.k] ? "border-emerald-300 bg-emerald-50/40" : "border-slate-200 hover:border-violet-300 hover:bg-violet-50/30"
                    }`}
                  >
                    <input type="file" accept=".csv" className="hidden" onChange={onFile(f.k)} />
                    <div className={`w-11 h-11 mx-auto rounded-xl flex items-center justify-center transition-colors ${
                      files[f.k] ? "bg-emerald-100 text-emerald-600" : "bg-slate-50 border border-slate-200 text-slate-400 group-hover:text-violet-500"
                    }`}>
                      {files[f.k] ? <CheckCircle2 size={21} /> : <FileSpreadsheet size={20} />}
                    </div>
                    <p className="text-sm font-semibold text-slate-800 mt-3">{f.t}</p>
                    <p className="text-xs text-slate-500 mt-0.5 leading-snug">{files[f.k] ? "✓ Cargado" : f.h}</p>
                    <p className="text-[11px] text-violet-500 mt-2 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                      {files[f.k] ? "cambiar archivo" : "tocá para subir el CSV"}
                    </p>
                  </label>
                ))}
              </div>

              <button
                onClick={cargarEjemplo}
                className="w-full mt-4 rounded-xl border border-slate-200 bg-slate-50/60 hover:bg-slate-50 px-4 py-2.5 text-left transition-colors group"
              >
                <p className="text-xs font-semibold text-slate-600 group-hover:text-violet-600 transition-colors">
                  ¿No tenés los archivos a mano? Probá con datos de ejemplo →
                </p>
                <p className="text-[11px] text-slate-500 mt-0.5">HATSU · 240 clientes · 2.160 pedidos</p>
              </button>

            </div>
          )}

          {/* 1 · Mapeo */}
          {current === P.mapeo && parseData && (
            <div>
              <PasoHeader i={P.mapeo} />
              <div className="space-y-4">
                {(["clientes", "pedidos"] as const).map((arch) => (
                  <div key={arch} className="rounded-2xl border border-slate-100 overflow-hidden">
                    <div className="px-4 py-2.5 bg-slate-50/70 border-b border-slate-100 flex items-center justify-between gap-2">
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Archivo de {arch}</p>
                      <span className="text-[11px] text-slate-400 tabular-nums inline-flex items-center gap-1">
                        <Users size={11} /> {num(parseData[arch].total)} filas
                      </span>
                    </div>
                    <div className="divide-y divide-slate-50">
                      {/* TODAS las columnas, no sólo las detectadas: si el operador no ve la columna
                          que la máquina ignoró, no puede corregirla. Y cada una es un <select>. */}
                      {parseData[arch].sugerencia.map((s: any) => {
                        const valor = mapeo[arch]?.[s.columnaCliente] ?? s.campoDeenex;
                        const asignado = valor && valor !== "(ignorar)";
                        // dos columnas al MISMO campo: antes ganaba la última en silencio y ambas
                        // filas seguían con el ✓ verde. Se marca en rojo y se bloquea el avance.
                        const enConflicto = asignado && (conflictosDe(arch).get(valor)?.length || 0) > 1;
                        return (
                          <div key={s.columnaCliente} className={`flex items-center gap-2 sm:gap-3 px-4 py-2.5 text-sm ${enConflicto ? "bg-red-50" : s.ambiguo ? "bg-amber-50/60" : ""}`}>
                            <div className="flex-1 min-w-0">
                              <p className="font-mono text-xs text-slate-600 truncate">{s.columnaCliente}</p>
                              {s.ejemplo && <p className="text-[10px] text-slate-400 truncate">ej: {s.ejemplo}</p>}
                            </div>
                            <ArrowRight size={13} className="text-slate-300 flex-shrink-0" />
                            <select
                              value={valor}
                              onChange={(e) => setMapeo((m) => ({ ...m, [arch]: { ...m[arch], [s.columnaCliente]: e.target.value } }))}
                              /* ring-violet-500 (4.4:1), no el 200 anterior (1.39:1): el foco de
                                 teclado tiene que verse. Son 10 selects, y con outline-none el
                                 anillo débil era el ÚNICO indicador — navegarlos a ciegas. */
                              className={`flex-1 min-w-0 rounded-lg border px-2 py-1.5 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 ${
                                enConflicto ? "border-red-400 bg-white font-semibold text-red-800" : s.ambiguo ? "border-amber-300 bg-white font-semibold" : asignado ? "border-slate-200 bg-white font-semibold text-slate-800" : "border-slate-200 bg-slate-50 text-slate-400"
                              }`}
                            >
                              <option value="(ignorar)">(ignorar)</option>
                              {(parseData.campos?.[arch] || []).map((c: any) => (
                                <option key={c.campo} value={c.campo}>{c.campo}{c.obligatorio ? " *" : ""}</option>
                              ))}
                            </select>
                            {enConflicto
                              ? <AlertTriangle size={14} className="text-red-500 flex-shrink-0" />
                              : s.ambiguo
                                ? <AlertTriangle size={14} className="text-amber-500 flex-shrink-0" />
                                : asignado
                                  ? <CheckCircle2 size={14} className="text-emerald-500 flex-shrink-0" />
                                  : <span className="w-3.5 flex-shrink-0" />}
                          </div>
                        );
                      })}
                    </div>
                    {conflictosDe(arch).size > 0 && (
                      <div className="px-4 py-2.5 bg-red-50 border-t border-red-100 flex items-start gap-2">
                        <AlertTriangle size={14} className="text-red-600 flex-shrink-0 mt-0.5" />
                        <p className="text-[11px] text-red-800">
                          {[...conflictosDe(arch)].map(([campo, cols]) => (
                            <span key={campo} className="block">
                              <strong>{campo}</strong> está asignado a {cols.length} columnas ({cols.join(", ")}): sólo una se usaría. Dejá una sola.
                            </span>
                          ))}
                        </p>
                      </div>
                    )}
                    {parseData[arch].sugerencia.some((s: any) => s.ambiguo) && (
                      <div className="px-4 py-2.5 bg-amber-50 border-t border-amber-100 flex items-start gap-2">
                        <AlertTriangle size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
                        <p className="text-[11px] text-amber-800">
                          Hay columnas que se parecen entre sí. Elegí vos cuál es cuál: si nos equivocamos acá,
                          toda la campaña se calcula sobre la columna equivocada.
                        </p>
                      </div>
                    )}
                  </div>
                ))}
                {/* Sin quién aceptó, WhatsApp oficial no le escribe a nadie: se dice acá, donde todavía se puede
                    elegir la columna que el auto-mapeo ignoró o volver con un archivo que la tenga. */}
                {!hayColumnaDeConsentimiento(parseData.clientes?.sugerencia, mapeo.clientes) && (() => {
                  const a = avisoSinColumnaConsentimiento();
                  return (
                    <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-3.5 flex items-start gap-2.5" role="status" data-testid="aviso-sin-consentimiento">
                      <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <p className="font-semibold text-amber-900">{a.titulo}</p>
                        <p className="text-amber-800 mt-0.5">
                          {a.detalle}{" "}
                          <Link to={RUTA_CARGA_BASE} className="font-semibold underline whitespace-nowrap">Cargar con consentimiento</Link>
                        </p>
                        {porQueCanales && <p className="text-xs text-amber-700 mt-1">{porQueCanales}</p>}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {/* 2 · Reporte */}
          {current === P.reporte && reporteFront && (
            <div>
              <PasoHeader i={P.reporte} />
              {/* Sólo si nadie puede recibir por el oficial: es el momento de volver con un archivo que diga quién
                  aceptó, antes de elegir la oferta y el canal. Con alguno, el número se ve en «Por dónde y cuándo». */}
              {alcanceOficial.alcance?.pueden === 0 && (
                <div className="mb-4"><AlcanceOficial estado={alcanceOficial} porQue={porQueCanales} /></div>
              )}
              <ReporteComercial
                reporte={reporteFront as any}
                /* el report REAL entero: de acá salen órdenes analizadas, ritmo, reparto y canal */
                real={report}
                /* la oferta de cada grupo: «Cupón 20% off» es lo que propone el motor, no lo que sale (ofertaEnReporte) */
                grupos={withAccent(grupos).map((g) => ({ ...g, oferta: ofertaEnReporte(g, campaign?.context) }))}
                sinContacto={report.sinContacto}
                /* con «declarada», a quien no dijo nada también se le escribe: el alcance lo dice según la política */
                politicaOptIn={politicaOptIn.cargando ? undefined : politicaOptIn.politica}
                onCompartir={doShare}
                onDescargarPDF={bajarReportePDF}
                descargandoPDF={descargandoPDF}
              />
              <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50/60 p-4 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-800">¿Los grupos están bien armados?</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Bajate el Excel con una hoja por grupo: quiénes cayeron en cada uno, por qué, y
                    quiénes quedaron a un paso de otro grupo. Es el momento de mirarlo — después de
                    acá se arman los mensajes.
                  </p>
                </div>
                <button
                  onClick={bajarSegmentacion}
                  disabled={bajandoSeg || !campaign?.id}
                  className="shrink-0 h-9 px-3 rounded-lg border border-violet-200 bg-white text-xs font-semibold text-violet-700 hover:bg-violet-50 inline-flex items-center gap-1.5 disabled:opacity-50"
                >
                  <Download size={13} /> {bajandoSeg ? "Armando…" : "Segmentación (Excel)"}
                </button>
              </div>
              {shareUrl && (
                <div className="mt-4 rounded-2xl border border-violet-200 bg-violet-50/50 p-4">
                  <p className="text-sm font-semibold text-violet-900 mb-1 inline-flex items-center gap-1.5">
                    {copied ? <><CheckCircle2 size={14} /> Link copiado</> : <><Copy size={14} /> Link público del reporte</>}
                  </p>
                  <p className="text-xs text-slate-500 mb-2">Cualquiera con el link lo ve, sin login. Sólo agregados: no expone datos personales. Si lo dejás de compartir, el link viejo deja de funcionar.</p>
                  <div className="flex items-center gap-2">
                    <input readOnly value={shareUrl} onFocus={(e) => e.currentTarget.select()} className="flex-1 min-w-0 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-mono text-slate-700" />
                    <a href={shareUrl} target="_blank" rel="noreferrer" className="rounded-lg bg-violet-600 text-white px-3 py-2 text-xs font-semibold hover:bg-violet-700 whitespace-nowrap inline-flex items-center gap-1">
                      Abrir <ExternalLink size={11} />
                    </a>
                    <button onClick={doUnshare} disabled={loading} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 hover:border-red-300 hover:text-red-600 whitespace-nowrap disabled:opacity-50">
                      Dejar de compartir
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 3 · Contexto */}
          {/* El contexto, en tres decisiones. Es el mismo formulario: cada paso muestra su parte. */}
          {current === P.tono && (
            <div>
              <PasoHeader i={P.tono} />
              {/* Con qué nombre salen los mensajes, a la vista y editable. Quedaba «Tu marca» sin que nada lo
                  mostrara (QA del 08/10): sin nombre se pide acá, y el paso del plan no deja armar los mensajes. */}
              {campaign && <div className="mb-4"><FirmaComercio campaignId={campaign.id} marca={campaign.marca} onGuardada={adoptarCampana} /></div>}
              {/* VozForm reemplaza a la sección "tono" de ContextoForm: el tono era la única
                  perilla y el ejemplo debajo era una frase escrita a mano que no coincidía con
                  ninguna plantilla real. Acá el ejemplo lo calcula el backend con el generador
                  de verdad, y se agregan el resto de los parámetros de la voz. */}
              <VozForm value={contexto} onChange={setContexto} marca={campaign?.marca} ensayo={ensayo} />
              <p className="text-xs text-slate-500 mt-3">Si después elegís mandar por WhatsApp oficial, el texto es el de la plantilla aprobada por Meta: la voz se usa en los mensajes por las líneas y por email.</p>
            </div>
          )}

          {current === P.oferta && (
            <div>
              <PasoHeader i={P.oferta} />
              {/* los grupos van para poder mostrar la CONSECUENCIA de los puntos (cuántos se
                  reparten en total y a cuánta gente), no sólo el número por cabeza */}
              {/* ticketDormido: ancla en pesos del techo (leído con ?. porque un report viejo no lo
                  trae → 0 → el techo se muestra sólo en %, sin espejo) */}
              <ContextoForm value={contexto} onChange={setContexto} seccion="oferta" grupos={grupos} ticketDormido={report?.dormidos?.ticketMediano ?? 0} />
            </div>
          )}

          {current === P.cuando && (
            <div>
              <PasoHeader i={P.cuando} />
              <fieldset className="mb-6">
                <legend className="text-sm font-semibold text-slate-800 mb-2">¿Por dónde sale esta campaña?</legend>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2.5">
                  {([
                    ["lineas", "Líneas por QR o email", "Activa escribe un mensaje para cada grupo y sale desde tus líneas de WhatsApp vinculadas y tus casillas.", Smartphone],
                    ["oficial", "WhatsApp oficial", "Sale con una plantilla aprobada por Meta. Los grupos de este análisis quedan como audiencia y tu oferta va en el dato «Oferta» de la plantilla.", BadgeCheck],
                    ["ambos", "Por los dos: QR y API", "Elegís cuántos WhatsApp salen por tus líneas y cuántos por WhatsApp oficial. Cada persona recibe por una sola vía.", Split],
                  ] as const).map(([valor, titulo, desc, Icono]) => {
                    const apagada = valor === "ambos" && !!sinPorLosDos;
                    return (
                    <label key={titulo} className={`flex items-start gap-3 rounded-xl border p-3.5 transition-all ${apagada ? "cursor-not-allowed opacity-70" : "cursor-pointer"} ${via === valor ? "border-violet-300 bg-violet-50" : "border-slate-200 bg-white hover:border-violet-200"}`}>
                      <input type="radio" name="via" checked={via === valor} disabled={apagada} onChange={() => elegirVia(valor)} className="mt-1 accent-violet-600" />
                      <span className="min-w-0">
                        <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-900"><Icono size={15} className="text-violet-500" /> {titulo}</span>
                        <span className="block text-xs text-slate-500 mt-0.5 leading-relaxed">{desc}</span>
                        {/* a cuántos les llega, ANTES de elegirlo: el número es el mismo que va a mostrar la Audiencia */}
                        {valor === "oficial" && !analisisDeEjemplo && alcanceOficial.alcance && (
                          <span className={`block text-xs font-semibold mt-1 ${alcanceOficial.alcance.pueden ? "text-violet-700" : "text-amber-700"}`} data-testid="alcance-oficial-tarjeta">
                            {alcanceOficial.alcance.pueden
                              ? `Le llega a hasta ${num(alcanceOficial.alcance.pueden)} de este análisis`
                              : "Con este análisis no le llega a nadie"}
                          </span>
                        )}
                        {apagada && <span className="block text-xs font-semibold mt-1 text-amber-700" data-testid="sin-por-los-dos">{sinPorLosDos}</span>}
                      </span>
                    </label>
                    );
                  })}
                </div>
                {/* el reparto quedó guardado de cuando se eligió «Por los dos»: al seguir por una sola vía se saca */}
                {via !== "ambos" && campaign?.reparto && (
                  <p className="text-xs text-amber-700 mt-2" data-testid="aviso-saca-reparto">
                    Esta campaña tenía un reparto entre QR y API ({resumenReparto(campaign.reparto)}). Al seguir por una sola vía, se saca.
                  </p>
                )}
              </fieldset>
              {porOficial ? (
                <div className="space-y-3">
                  {/* Cuántos pueden recibir, por qué no los demás y, con 0, el otro canal: antes se seguía igual y la
                      campaña oficial fallaba recién al confirmar. */}
                  {!analisisDeEjemplo && <AlcanceOficial estado={alcanceOficial} porQue={porQueCanales} onOtroCanal={() => setPorOficial(false)} />}
                  <div className="rounded-2xl border border-slate-100 bg-slate-50/60 p-4 text-sm text-slate-600 space-y-1.5">
                    {/* con nadie a quien escribirle no hay «al seguir»: el aviso de arriba dice qué hacer */}
                    {!oficialSinNadie && <p>Al seguir, guardamos la oferta que elegiste y abrimos una campaña de WhatsApp oficial con los grupos de este análisis. Ahí elegís la plantilla, probás y confirmás.</p>}
                    <p className="text-xs text-slate-500">El horario y el ritmo del oficial se configuran en WhatsApp → Configuración → Envío. Sólo reciben quienes dieron su consentimiento.</p>
                    {analisisDeEjemplo && (
                      <p className="text-xs font-medium text-amber-700">Son datos de ejemplo: sus teléfonos son de personas ajenas a la marca y no se mandan por WhatsApp oficial.</p>
                    )}
                  </div>
                </div>
              ) : repartido && campaign ? (
                <div className="space-y-4">
                  <RepartoWhatsApp
                    campaignId={campaign.id}
                    onCambio={(e) => { setEstadoReparto(e); setCampaign((c: any) => (c ? { ...c, reparto: e.reparto } : c)); }}
                    onAbrirApi={analisisDeEjemplo ? undefined : doParteApi}
                    abriendoApi={loading}
                  />
                  <div className="rounded-2xl border border-slate-100 bg-slate-50/60 p-4 text-sm text-slate-600 space-y-1.5">
                    <p>Este asistente sigue con la parte por QR: los mensajes, las líneas y el envío. La parte de la API se manda desde WhatsApp oficial, con una plantilla: la abrís desde el panel de arriba o desde el detalle de la campaña.</p>
                    <p className="text-xs text-slate-500">El horario de abajo es el de las líneas; el del oficial se configura en WhatsApp → Configuración → Envío. La fatiga es una sola: la regla de la marca suma lo que sale por las dos vías.</p>
                  </div>
                  <ContextoForm value={contexto} onChange={setContexto} seccion="envio" reglaMarca={reglaMarca} />
                </div>
              ) : (
                <ContextoForm value={contexto} onChange={setContexto} seccion="envio" reglaMarca={reglaMarca} />
              )}
            </div>
          )}

          {/* 4 · Plan */}
          {current === P.plan && (() => {
            /* La tabla fría de 5 columnas trataba a los 4 grupos como iguales y escondía lo que
               explica la segmentación: quiénes son (perfil), cuánto pesan (% de facturación) y por
               qué esa campaña. Se cruzan los grupos con `repartoIngreso` del reporte y se ordenan
               por prioridad y peso, para que la importancia se lea sola. */
            const reparto: Record<string, number> = {};
            for (const r of (campaign?.report?.repartoIngreso || [])) reparto[r.grupoId] = r.pct;
            const totalPersonas = grupos.reduce((s: number, g: any) => s + (g.personas || 0), 0);
            const ordenPrio: Record<string, number> = { Alta: 0, Media: 1, Baja: 2 };
            const enriq = grupos
              .map((g: any) => ({ ...g, pct: reparto[g.id] ?? 0, accent: ACCENTS[g.id] || ACCENTS.g_riesgo }))
              .sort((a: any, b: any) => (ordenPrio[a.prioridad] ?? 9) - (ordenPrio[b.prioridad] ?? 9) || b.pct - a.pct);
            const porPct = [...enriq].sort((a: any, b: any) => b.pct - a.pct);
            // "los que ya se fueron o se están yendo" = dormidos + en riesgo (ids fijos del motor)
            const pctIdos = (reparto.g_dormidos ?? 0) + (reparto.g_riesgo ?? 0);
            return (
              <div>
                <PasoHeader i={P.plan} />
                {/* justo antes de «Armar los mensajes»: con qué nombre van a salir (y, si falta, dónde ponerlo) */}
                {campaign && <div className="mb-4"><FirmaComercio campaignId={campaign.id} marca={campaign.marca} onGuardada={adoptarCampana} /></div>}

                {/* ── Cómo se reparte tu facturación (el momento "ah, entiendo mi negocio") ── */}
                <div className="rounded-2xl border border-slate-100 p-5 mb-4">
                  <h3 className="text-base font-semibold text-slate-900">
                    Tu base son {num(totalPersonas)} personas. Así se reparte tu facturación.
                  </h3>
                  {/* barra apilada: un segmento por grupo, sin texto adentro (el 2% se rompería) */}
                  <div className="h-3 rounded-full overflow-hidden flex w-full mt-3.5 bg-slate-100">
                    {porPct.filter((g: any) => g.pct > 0).map((g: any) => (
                      <div key={g.id} className={g.accent.dot} style={{ width: `${g.pct}%` }} title={`${g.nombre} · ${g.pct}%`} />
                    ))}
                  </div>
                  {/* leyenda en dos columnas */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 mt-3">
                    {porPct.map((g: any) => (
                      <div key={g.id} className="flex items-center gap-1.5 text-xs min-w-0">
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${g.accent.dot}`} />
                        <span className="text-slate-600 truncate">{g.emoji} {g.nombre}</span>
                        <span className="ml-auto tabular-nums font-semibold text-slate-700 flex-shrink-0">{g.pct}%</span>
                      </div>
                    ))}
                  </div>
                  {pctIdos > 0 && (
                    <p className="text-sm text-slate-600 mt-4 leading-relaxed">
                      <span className="font-bold text-violet-700">El {pctIdos}% de tu plata</span> viene de clientes que ya se
                      fueron o se están yendo. La buena noticia: todavía los podés traer de vuelta.
                    </p>
                  )}
                </div>

                {/* ── Una ficha por grupo (no tabla): perfil + peso + por qué esta oferta ── */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {enriq.map((g: any) => (
                    <div key={g.id} className={`rounded-2xl border border-slate-100 border-l-4 ${g.accent.borderL} p-4`}>
                      {/* items-start + sin truncate: en mobile angosto el nombre del grupo —lo más
                          importante de la ficha— envuelve en dos líneas en vez de cortarse a
                          "Dormidos de a…"; la pill queda arriba a la derecha. */}
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-semibold text-slate-800 leading-tight">{g.emoji} {g.nombre}</p>
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full flex-shrink-0 whitespace-nowrap ${g.prioridad === "Alta" ? g.accent.pillAlta : `${g.accent.bg} ${g.accent.text}`}`}>
                          Prioridad {g.prioridad === "Alta" ? "alta" : "media"}
                        </span>
                      </div>

                      {/* el perfil: el dato que estaba 100% oculto, el "esto lo pensó alguien" */}
                      <p className="text-sm text-slate-600 mt-2 leading-relaxed">{g.perfil}</p>

                      <div className="grid grid-cols-2 divide-x divide-slate-100 mt-3">
                        <div>
                          <p className="text-2xl font-bold tabular-nums text-slate-900 leading-none">{num(g.personas)}</p>
                          <p className="text-xs text-slate-500 mt-1">{plural(g.personas, "persona", "personas")}</p>
                        </div>
                        <div className="pl-4">
                          <p className={`text-2xl font-bold tabular-nums leading-none ${g.accent.strong}`}>{g.pct}%</p>
                          <p className="text-xs text-slate-500 mt-1">de tu facturación</p>
                          {g.valor > 0 && <p className="text-[11px] text-slate-500 mt-0.5">≈ {compact(g.valor)} · histórico</p>}
                        </div>
                      </div>

                      <div className="border-t border-slate-50 pt-3 mt-3">
                        <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-1.5">Qué le mandamos y por qué</p>
                        <div className="flex flex-wrap gap-1.5">
                          {[g.campana, chipOferta(g, campaign?.context ?? contexto), g.canal].map((c: string, i: number) => (
                            <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{c}</span>
                          ))}
                        </div>
                        {/* la razón depende de la oferta elegida: sin descuentos no promete beneficio ni cupón (razonDelGrupo) */}
                        {razonDelGrupo(g.id, campaign?.context ?? contexto) && <p className="text-xs text-slate-500 mt-2 leading-relaxed">{razonDelGrupo(g.id, campaign?.context ?? contexto)}</p>}
                      </div>
                    </div>
                  ))}
                </div>

                {/* dos verdades que el dueño necesita antes de armar los mensajes */}
                <div className="mt-4 space-y-1">
                  <p className="text-xs text-slate-500">Cada persona entra en un solo grupo y recibe una sola campaña. Nadie recibe dos mensajes.</p>
                  <p className="text-xs text-slate-500">Los montos son ventas reales de tu base, sin ajustar por inflación.</p>
                </div>
              </div>
            );
          })()}

          {/* 5 · Mensajes */}
          {current === P.mensajes && (
            <div>
              <PasoHeader i={P.mensajes} />
              {/* sólo si el techo recortó algo: con el contexto por defecto (15% elegido, techo 15) lo que baja a los
                  Dormidos de 20 a 15 es el % que eligió la marca, y el cartel decía que se había recortado por el techo */}
              {grupos.some((g: any) => porQueBajoElDescuento(g, campaign?.context ?? contexto)?.causa === "techo") && (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50/50 p-4 flex items-start gap-3 mb-4">
                  <ShieldCheck size={18} className="text-emerald-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-emerald-900">Techo del {techo}% respetado</p>
                    <p className="text-xs text-emerald-700 mt-0.5">Se recortaron las ofertas que lo superaban.</p>
                  </div>
                </div>
              )}
              {/* La previa la trae el backend YA renderizada: acá se mostraba la plantilla cruda,
                  con "{nombre}" y "{vence}" a la vista — o sea, un texto que nadie iba a recibir. */}
              {campaign && <VistaPreviaMensajes campaignId={campaign.id} seleccion={gruposElegidos} onSeleccion={setGruposElegidos} />}
            </div>
          )}

          {/* 6 · Envío */}
          {/* Un paso por canal. El de email es informativo (la casilla se configura en su propia
              pantalla, con credenciales SMTP), pero ahora tiene su lugar en el recorrido en vez
              de ser un cartel amarillo apretado contra el botón de enviar. */}
          {current === P.canales && (
            <div>
              <PasoHeader i={P.canales} />
              <div className="space-y-6">
                {usaWhatsApp && (
                  <section aria-label="WhatsApp">
                    {/* sin título propio: ConectarCanal ya trae el suyo («WhatsApp») */}
                    <ConectarCanal canal="whatsapp" seleccionable onLineasSeleccionadas={setLineasSel} onCambio={() => { setError(""); setErrorCode(""); }} />
                  </section>
                )}
                {usaEmail && (
                  <section aria-label="Email">
                    {usaWhatsApp && <h3 className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-1.5"><Mail size={15} className="text-slate-400" /> Email</h3>}
                    {/* La casilla se configura ACÁ MISMO, sin salir del flujo — el mismo componente que
                        la pantalla dedicada, así WhatsApp y email se conectan igual dentro del wizard.
                        onCambio limpia el error del envío para que revalide tras verificar. */}
                    <GestionCasillas embebido onCambio={() => { setError(""); setErrorCode(""); }} />
                    <p className="text-[11px] text-slate-500 mt-3 flex items-start gap-1.5">
                      <ShieldCheck size={12} className="flex-shrink-0 mt-0.5" />
                      La casilla queda guardada para tu marca: la conectás una vez y sirve para todas tus campañas.
                      También la podés administrar desde <Link to="/activa/email" className="text-violet-500 hover:underline">Casillas de email</Link>.
                    </p>
                  </section>
                )}
              </div>
            </div>
          )}

          {current === P.envio && (
            <div>
              <PasoHeader i={P.envio} />
              {/* una campaña vieja que quedó firmada «Tu marca»: el botón lo dice (bloqueoEnvio) y acá se completa */}
              {campaign && sinMarca && <div className="mb-4"><FirmaComercio campaignId={campaign.id} marca={campaign.marca} onGuardada={adoptarCampana} /></div>}
              <div className="rounded-2xl border border-slate-100 bg-slate-50/50 p-4 space-y-2.5">
                {[
                  queSeSalteaPorConsentimiento(politicaOptIn.cargando ? null : politicaOptIn.politica),
                  "Un solo mensaje por número, aunque esté repetido en la base.",
                  "Sale de a poco por cada línea, con pausas: no sale todo de golpe.",
                  "Se registra el estado de cada mensaje, uno por uno.",
                ].map((t) => (
                  <p key={t} className="text-xs text-slate-600 flex items-start gap-2">
                    <CheckCircle2 size={13} className="text-emerald-500 flex-shrink-0 mt-0.5" /> {t}
                  </p>
                ))}
              </div>

              {/* Los canales ya tienen su propio paso: acá va el RESUMEN de lo que se resolvió,
                  con la salida a arreglarlo sin perder el wizard. Repetir el panel entero hacía
                  que el paso de envío fuera otra pantalla de configuración en vez del momento de
                  apretar el botón. */}
              <div className="mt-4 rounded-2xl border border-slate-100 bg-white p-4">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2.5">Por dónde va a salir</p>
                <div className="space-y-1.5 text-sm">
                  {usaWhatsApp && (
                    <p className="flex items-center gap-2 text-slate-600">
                      <Smartphone size={14} className="text-slate-400 flex-shrink-0" /> WhatsApp
                      <button onClick={() => go(P.canales)} className="ml-auto text-xs font-semibold text-violet-600 hover:text-violet-700">Revisar</button>
                    </p>
                  )}
                  {usaEmail && (
                    <p className="flex items-center gap-2 text-slate-600">
                      <Mail size={14} className="text-slate-400 flex-shrink-0" /> Email
                      <button onClick={() => go(P.canales)} className="ml-auto text-xs font-semibold text-violet-600 hover:text-violet-700">Revisar</button>
                    </p>
                  )}
                  {/* repartida: este envío es sólo la parte de QR, y la de la API no sale sola */}
                  {campaign?.reparto && (
                    <p className="flex items-start gap-2 text-slate-600" data-testid="envio-reparto">
                      <Split size={14} className="text-violet-500 flex-shrink-0 mt-0.5" />
                      <span className="min-w-0">
                        Repartida: por tus líneas salen hasta {num(campaign.reparto.qr)}.
                        {campaign.reparto.api > 0 && <> Los {num(campaign.reparto.api)} de la API se mandan aparte, desde WhatsApp oficial.</>}
                      </span>
                      {campaign.reparto.api > 0 && !analisisDeEjemplo && (
                        <button onClick={doParteApi} disabled={loading} className="ml-auto text-xs font-semibold text-violet-600 hover:text-violet-700 whitespace-nowrap disabled:opacity-50">Seguir la parte de la API</button>
                      )}
                    </p>
                  )}
                </div>
              </div>

              {/* el ejemplo tampoco se programa: el backend lo rechazaría con EJEMPLO */}
              {campaign && !campaign.esEjemplo && !esEjemplo && (
                <div className="mt-4">
                  <ProgramarEnvio
                    campaignId={campaign.id}
                    // Misma traducción que doSend (`seleccionApi`): todos elegidos → undefined, si no
                    // el subconjunto. Con 0 elegidos la lista queda vacía y `sinGrupos` deshabilita el
                    // botón (el componente además se niega a programar con []): el CTA de arriba no
                    // frena este botón, que vive aparte.
                    grupos={seleccionApi().grupos}
                    sinGrupos={sinGrupos}
                    lineas={lineasSel.todas ? [] : lineasSel.ids}
                    programadoPara={campaign.estado === "programada" ? campaign.programadoPara : null}
                    programacionVencida={campaign.programacionVencida}
                    onCambio={async () => {
                      const c = await activaApi.campaign(campaign.id).catch(() => null);
                      if (c?.data) setCampaign(c.data);
                    }}
                  />
                </div>
              )}
            </div>
          )}

          {/* 7 · Resultados */}
          {current === P.resultados && (
            <div>
              <PasoHeader i={P.resultados} />

              {sendResult && (
                <div className="space-y-2.5 mb-4">
                  {sendResult.encolados != null ? (() => {
                    // del estado REAL de la campaña (lo actualiza el seguimiento), no de la respuesta congelada
                    // del momento de lanzar: antes decía «en la fila de envío» con todo ya enviado (QA del 08/10)
                    const r = resumenDelEnvio(sendResult, estadoEnvio, campaign?.stats?.pendientes);
                    return (
                      <div className={`rounded-xl border p-3.5 text-sm ${r.tono === "aviso" ? "border-amber-200 bg-amber-50/60 text-amber-900" : "border-emerald-200 bg-emerald-50/50 text-emerald-800"}`}>
                        <p className="font-semibold">{r.titulo}{r.enCurso && (sendResult.lineasWhatsApp ?? 0) > 0 && <> · saliendo desde {sendResult.lineasWhatsApp} {plural(sendResult.lineasWhatsApp, "línea", "líneas")} de WhatsApp</>}</p>
                        {r.detalle && <p className={`text-xs mt-0.5 ${r.tono === "aviso" ? "text-amber-800" : "text-emerald-700"}`}>{r.detalle}</p>}
                      </div>
                    );
                  })() : sendResult.mensaje ? (
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3.5 text-sm text-slate-600">{sendResult.mensaje}</div>
                  ) : null}
                  {/* Son los de ESTE envío, no los «nunca se les preguntó» del reporte: `avisoSinPermiso` cuenta por qué. */}
                  <AvisoSinPermiso n={sendResult.sinOptIn} />
                </div>
              )}

              {enviando ? (
                <div className="flex flex-col items-center justify-center py-14 text-center">
                  <Loader2 size={28} className="animate-spin text-emerald-600" />
                  <p className="text-sm font-semibold text-slate-700 mt-4">Enviando la campaña…</p>
                  <p className="text-xs text-slate-500 mt-1 max-w-xs">Los mensajes salen de a poco, con pausas entre uno y otro para que WhatsApp no marque la línea: con muchos destinatarios puede tardar horas. Podés cerrar esta pantalla, el envío sigue.</p>
                </div>
              ) : results ? (
                <div className="space-y-4">
                  {/* si el transporte fue simulado, esto no son entregas: decirlo antes que nada */}
                  {results.hayDatosSimulados && (
                    <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-3.5 flex items-start gap-2.5">
                      <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-bold text-amber-900">{num(results.eventosSimulados)} {plural(results.eventosSimulados, "mensaje NO salió", "mensajes NO salieron")} de verdad</p>
                        <p className="text-xs text-amber-800 mt-0.5">Envío de prueba (WhatsApp en modo de prueba, o email sin una casilla conectada). Sirve para ver que todo funciona, pero no son entregas reales.</p>
                      </div>
                    </div>
                  )}

                  <DeliveryFunnel total={results.total} atribucionPendiente={atribucionPendiente} simulado={results.hayDatosSimulados} />

                  {/* 6 columnas no entran en un panel angosto: con un min-w chico la tabla no
                      scrollea y la última columna ("Volvieron") se corta contra el borde. */}
                  <div className="rounded-2xl border border-slate-100 overflow-x-auto">
                    <table className="w-full min-w-[760px] text-sm">
                      <thead>
                        <tr className="bg-slate-50/70 text-[11px] uppercase tracking-wider text-slate-400 text-left">
                          <th className="px-4 py-3">Grupo</th>
                          <th className="px-4 py-3 text-right">Enviados</th>
                          <th className="px-4 py-3 text-right">Entregados</th>
                          <th className="px-4 py-3 text-right">Leídos</th>
                          <th className="px-4 py-3 text-right">Respondieron</th>
                          <th className="px-4 py-3 text-right">Volvieron</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                        {(results.porGrupo || []).map((r: any) => (
                          <tr key={r.grupoId} className="hover:bg-slate-50/50">
                            <td className="px-4 py-3 font-semibold text-slate-800 whitespace-nowrap">{r.emoji} {r.nombre}</td>
                            <td className="px-4 py-3 text-right tabular-nums text-slate-700">{num(r.enviados)}</td>
                            <td className="px-4 py-3 text-right tabular-nums text-slate-600">{num(r.entregados ?? 0)}</td>
                            <td className="px-4 py-3 text-right tabular-nums text-sky-600 font-medium">{num(r.leidos ?? 0)}</td>
                            <td className="px-4 py-3 text-right tabular-nums text-violet-600">{num(r.respondieron)}</td>
                            <td className="px-4 py-3 text-right tabular-nums font-semibold text-emerald-600">
                              {atribucionPendiente ? <span className="text-[10px] font-bold uppercase text-slate-400">sin medir</span> : num(r.volvieron)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {/* la tabla scrollea, pero eso no se descubre solo: avisarlo donde el ancho no alcanza */}
                  <p className="text-[11px] text-slate-400 lg:hidden -mt-2">Deslizá la tabla para ver todas las columnas →</p>

                  {/* "volvieron" no se puede saber sin una base posterior: no fabricamos un 0 */}
                  {atribucionPendiente ? (
                    <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                      <div className="flex items-start gap-3">
                        <TrendingUp size={17} className="text-slate-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-bold text-slate-800">Cerrá la medición: subí la base del período siguiente</p>
                          <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                            Pedile al comercio sus 2 archivos (pedidos y clientes) <strong>actualizados y completos</strong> (todos los pedidos, con las compras nuevas). Comparamos persona por persona quién volvió a comprar y cuánto gastó después del envío.
                          </p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2.5 mt-3">
                        {([["pedidos", "Pedidos actualizados"], ["clientes", "Clientes actualizados"]] as const).map(([k, label]) => (
                          <label key={k} className={`rounded-xl border px-3 py-2.5 text-center cursor-pointer transition-colors text-xs font-semibold ${segFiles[k] ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-600 hover:border-violet-300"}`}>
                            <input type="file" accept=".csv" className="hidden" onChange={onSegFile(k)} />
                            {segFiles[k] ? "✓ " : ""}{label}
                          </label>
                        ))}
                      </div>
                      <button onClick={doSeguimiento} disabled={loading || !segFiles.pedidos || !segFiles.clientes} className="mt-2.5 h-9 w-full rounded-lg bg-violet-600 text-white text-xs font-bold hover:bg-violet-700 disabled:opacity-40 inline-flex items-center justify-center gap-1.5">
                        {loading ? <Loader2 size={12} className="animate-spin" /> : <UploadCloud size={12} />} Medir el resultado
                      </button>
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-emerald-200 bg-emerald-50/50 p-4 flex items-center justify-between gap-3 flex-wrap">
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Volvieron a comprar</p>
                        <p className="text-2xl font-black text-emerald-800 tabular-nums">
                          {num(results.total.volvieron)}<span className="text-sm font-bold text-emerald-600 ml-1.5">de {num(results.total.enviados)}</span>
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 inline-flex items-center gap-1.5">
                          {/* decía «Plata recuperada»: es lo que compraron después del envío, sin grupo de control (29/10) */}
                          Facturaron los que la recibieron
                          {results.plataParcial && <span className="text-[9px] px-1 py-0.5 rounded bg-amber-50 text-amber-600" title="Algunos que volvieron no traían base para calcular cuánto gastaron: el real es mayor">Parcial</span>}
                        </p>
                        <p className="text-xl font-bold text-emerald-700 tabular-nums">{compact(results.total.plataRecuperada)}</p>
                      </div>
                      <p data-testid="aclaracion-sin-control" className="basis-full text-[11px] text-slate-500 leading-relaxed">
                        Incluye a quienes habrían comprado igual: esta campaña no se compara contra un grupo que no la recibió.
                      </p>
                    </div>
                  )}

                </div>
              ) : (
                <p className="text-sm text-slate-400 py-10 text-center">Todavía no hay resultados.</p>
              )}
            </div>
          )}

          {/* Respuestas: adentro de Resultados, una vez que el envío terminó */}
          {current === P.resultados && !enviando && results && (
            <section aria-label="Respuestas" className="mt-8 pt-6 border-t border-slate-100">
              <div className="flex items-center justify-between gap-3 mb-3">
                <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2"><MessageSquare size={16} className="text-slate-400" /> Lo que te contestaron</h3>
                <button type="button" onClick={() => void cargarRespuestas()} disabled={cargandoRespuestas}
                  className="text-xs font-semibold text-violet-600 hover:text-violet-700 disabled:opacity-50 inline-flex items-center gap-1">
                  {cargandoRespuestas && <Loader2 size={12} className="animate-spin" />} Actualizar
                </button>
              </div>
              {responses.length === 0 ? (
                <div className="py-10 text-center">
                  <div className="w-11 h-11 rounded-xl bg-slate-50 text-slate-300 flex items-center justify-center mx-auto">
                    <MessageSquare size={20} />
                  </div>
                  <p className="text-sm font-semibold text-slate-600 mt-3">Todavía nadie respondió</p>
                  <p className="text-xs text-slate-500 mt-1 max-w-xs mx-auto leading-relaxed">
                    Las respuestas aparecen acá a medida que llegan. Se contestan desde el WhatsApp del local. Quien responde
                    BAJA por WhatsApp o se da de baja desde el link del mail queda dado de baja solo. Las bajas que
                    lleguen por otro medio se cargan a mano en Bajas.
                  </p>
                </div>
              ) : (
                <div className="space-y-2.5">
                  {responses.slice(0, 12).map((r, i) => (
                    <div key={r.id || i} className="rounded-xl border border-slate-100 bg-slate-50/40 p-3.5">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold text-slate-800 truncate">{r.persona}</span>
                        {/* El badge se DERIVA del dato, no del campo `estado`: esta lista solo trae
                            conversaciones con inbound (baja o respondida), así que quien contestó y no
                            se dio de baja es un lead que RESPONDIÓ, no "sin responder". */}
                        {(() => {
                          const esBaja = r.estado === "baja";
                          const contesto = !!r.respuesta?.trim();
                          const cls = esBaja ? "bg-red-50 text-red-600" : contesto ? "bg-violet-50 text-violet-700" : "bg-slate-100 text-slate-500";
                          const txt = esBaja ? "pidió baja" : contesto ? "respondió" : "sin responder";
                          return <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${cls}`}>{txt}</span>;
                        })()}
                      </div>
                      <p className="text-sm text-slate-600 mt-1">"{r.respuesta}"</p>
                      {/* sólo lo que existe de verdad: campana/oferta vienen vacíos del backend */}
                      <p className="text-[11px] text-slate-400 mt-1.5">{[r.grupo, r.linea].filter(Boolean).join(" · ") || "—"}</p>
                    </div>
                  ))}
                  {responses.length > 12 && <p className="text-xs text-slate-400 text-center pt-1">y {responses.length - 12} más</p>}
                </div>
              )}
              <div className="mt-5 pt-5 border-t border-slate-50 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {campaign?.id && (
                  <Link to={`/activa/campanas/${campaign.id}`} className="h-12 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 inline-flex items-center justify-center gap-2">
                    Ver la campaña <ArrowRight size={16} />
                  </Link>
                )}
                <Link to="/activa" className="h-12 rounded-xl border border-slate-200 bg-white text-slate-600 text-sm font-semibold hover:bg-slate-50 inline-flex items-center justify-center">
                  Volver a Campañas
                </Link>
              </div>
            </section>
          )}
        </div>

        {/*
          Barra de navegación ANCLADA al piso. Queda pegada abajo mientras el paso scrollea: el
          reporte y los mensajes miden más de una pantalla, y tener que bajar hasta el fondo para
          encontrar el botón (o para volver) rompe el ritmo del flujo.

          Va de borde a borde (los -mx compensan el padding del contenedor) y con borde superior:
          flotando con margen y esquinas redondeadas parecía una tarjeta suelta en medio del
          contenido. Una barra pegada al piso se lee como parte del marco de la app, no como algo
          que se te cruzó por delante.

          El "atrás" es chico y secundario a propósito: se usa poco, pero tiene que estar.
        */}
      </div>

      {/*
        Barra anclada al piso del área de contenido.
        · -mx-* → va de borde a borde (come el padding lateral de la página) en vez de quedar
          con el ancho del contenido: flotando con márgenes parecía una tarjeta suelta.
        · -mb-* → come el padding inferior de la página, para que al llegar al final del scroll
          el sticky no quede flotando sobre una franja gris.
      */}
      {/* mt-auto: con el paso corto (sin scroll) el sticky no tiene nada que "pegar" y la barra
          quedaría flotando a media pantalla, donde termina el contenido. El auto la manda al piso. */}
      <div className="sticky bottom-0 z-20 -mx-4 sm:-mx-6 md:-mx-8 mt-auto pt-5 border-t border-slate-200/80 bg-white/90 backdrop-blur-md px-4 sm:px-6 md:px-8 py-3 shadow-[0_-4px_24px_-12px_rgba(15,23,42,0.18)]">
          <div className="mx-auto max-w-4xl">
            {/* El mensaje ejemplo, pegado arriba del CTA mientras se configura tono/oferta: el
                impacto de cada cambio queda a la vista sin volver a scrollear hasta el preview. */}
            {verAsiSuena && (
              <div className="mb-2.5">
                <AsiSuena ensayo={ensayo} cargando={ensayoCargando} />
              </div>
            )}
          <div className="flex items-center gap-2.5">
            <button
              type="button"
              onClick={atras}
              title={current === P.carga ? "Volver a Campañas" : `Volver a: ${STEPS[current - 1]?.label}`}
              aria-label={current === P.carga ? "Volver a Campañas" : `Volver al paso anterior: ${STEPS[current - 1]?.label}`}
              className="h-11 w-11 flex-shrink-0 rounded-xl border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-800 hover:border-slate-300 active:scale-95 inline-flex items-center justify-center transition-all focus-visible:ring-2 focus-visible:ring-violet-400"
            >
              <ArrowLeft size={17} />
            </button>

            {accion ? (
              <div className="flex-1 min-w-0">
                <CTA onClick={accion.onClick} disabled={accion.disabled} loading={loading} tono={accion.tono}>
                  {accion.label}
                </CTA>
              </div>
            ) : (
              // último paso (o envío en curso): no hay "siguiente" que ofrecer, y no se inventa uno
              <p className="flex-1 text-center text-xs text-slate-400 px-2">
                {enviando ? "Enviando…" : current === P.resultados && results ? "Terminaste el flujo" : " "}
              </p>
            )}
          </div>
          </div>
      </div>

      {confirmarEnvio && campaign && (
        <ConfirmarEnvio
          campaignId={campaign.id}
          {...seleccionApi()}
          titulo="¿Lanzar la campaña?"
          onConfirmar={() => { setConfirmarEnvio(false); doSend(); }}
          onCerrar={() => setConfirmarEnvio(false)}
          // sin líneas el botón se bloquea: esta es la salida a conectar una sin salir del wizard
          onConectarLinea={() => { setConfirmarEnvio(false); go(P.canales); }}
        />
      )}
    </div>
  );
}
